/**
 * Undo/Redo utilities for canvas operations
 * Extracted from MyCanvas.js to reduce file size
 */

import { flushSync } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { createStateHash } from "./keyboardStateUtils";
import { createImageLoadPromise } from "./imageLoadingUtils";

/**
 * Compares two canvas states to check if they're effectively the same
 * @param {Object} state1 - First state
 * @param {Object} state2 - Second state
 * @returns {boolean} True if states are the same, false otherwise
 */
function areStatesEqual(state1, state2) {
  if (!state1 || !state2) return false;

  try {
    const hash1 = createStateHash(state1);
    const hash2 = createStateHash(state2);
    return hash1 === hash2;
  } catch (error) {
    // Fallback: compare lengths and image/offCanvasImage positions so we don't skip distinct states
    // (e.g. two element_edit_complete with same image at different positions must not be equal)
    // Compare presence/length of important main/off-canvas arrays
    const keysToCompare = [
      "rectangles",
      "offCanvasRectangles",
      "circles",
      "offCanvasCircles",
      "texts",
      "offCanvasTexts",
      "arrows",
      "offCanvasArrows",
      "lines",
      "offCanvasLines",
      "images",
      "offCanvasImages",
    ];

    for (const key of keysToCompare) {
      const len1 = state1[key]?.length || 0;
      const len2 = state2[key]?.length || 0;
      if (len1 !== len2) return false;
    }

    // Helper to serialize element positions (id,x,y) for comparison
    const pos = (arr) =>
      (arr || [])
        .map((i) => `${i.id}-${Number(i.x || 0)}-${Number(i.y || 0)}`)
        .join("|");

    // Compare positions for shapes and images on both main and off-canvas arrays
    const checks = [
      [state1.rectangles, state2.rectangles],
      [state1.offCanvasRectangles, state2.offCanvasRectangles],
      [state1.circles, state2.circles],
      [state1.offCanvasCircles, state2.offCanvasCircles],
      [state1.images, state2.images],
      [state1.offCanvasImages, state2.offCanvasImages],
    ];

    for (const [a, b] of checks) {
      if (pos(a) !== pos(b)) return false;
    }

    // If we reach here, fallback considers the states equal
    return true;
  }
}

/**
 * Creates an undo function that restores the previous canvas state
 * @param {Array} history - Current history array
 * @param {Function} setHistory - Function to set history state
 * @param {Function} setRedoStack - Function to set redo stack state
 * @param {React.RefObject} isUndoingRedoing - Ref to track if undo/redo is in progress
 * @param {string} fillColor - Current fill color
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setSelectedTextId - Setter for selected text ID
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedArrow - Setter for selected arrow
 * @param {Function} setSelectedLine - Setter for selected line
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} setLines - Setter for lines array
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setVideos - Setter for videos array
 * @param {Function} setScribbles - Setter for scribbles array
 * @param {Function} setOffCanvasTexts - Setter for off-canvas texts array
 * @param {Function} setOffCanvasRectangles - Setter for off-canvas rectangles array
 * @param {Function} setOffCanvasCircles - Setter for off-canvas circles array
 * @param {Function} setOffCanvasArrows - Setter for off-canvas arrows array
 * @param {Function} setOffCanvasDoubleSidedArrows - Setter for off-canvas double-sided arrows array
 * @param {Function} setOffCanvasImages - Setter for off-canvas images array
 * @param {Function} setOffCanvasScribbles - Setter for off-canvas scribbles array
 * @param {Function} setOffCanvasLines - Setter for off-canvas lines array
 * @param {Function} setOffCanvasVideos - Setter for off-canvas videos array
 * @param {Function} setImages - Setter for images array
 * @returns {Function} Undo function
 */
export function createUndo({
  history,
  setHistory,
  setRedoStack,
  isUndoingRedoing,
  lastUndoRedoCompletionTime,
  fillColor,
  setSelectedObject,
  setSelectedText,
  setSelectedTextId,
  setSelectedImage,
  setSelectedArrow,
  setSelectedLine,
  setSelectedObjects,
  setArrows,
  setLines,
  setRectangles,
  setCircles,
  setDoubleSidedArrows,
  setTexts,
  setVideos,
  setScribbles,
  setOffCanvasTexts,
  setOffCanvasRectangles,
  setOffCanvasCircles,
  setOffCanvasArrows,
  setOffCanvasDoubleSidedArrows,
  setOffCanvasImages,
  setOffCanvasScribbles,
  setOffCanvasLines,
  setOffCanvasVideos,
  setImages,
  images, // Current images state - needed to preserve image objects
  offCanvasImages, // Current off-canvas images (fallback)
  offCanvasImagesRef, // Ref updated each render - use at undo time so we get latest, not stale closure
  rectangles, // Current rectangles state - needed to preserve rectangles
  circles, // Current circles state - needed to preserve circles
  offCanvasRectangles, // Current off-canvas rectangles - fallback when history saved them empty
  offCanvasCircles, // Current off-canvas circles - fallback when history saved them empty
  setCanvasHeight,
}) {
  return function undo(offCanvasSnapshotAtClick) {
    // DEBUG: Log immediately when undo is called
    // CRITICAL: Allow undo even when at initial state if there are images
    // This handles the case where we've restored the original image from add_preset_image
    // and want to undo the add_preset_image operation (clear the image)
    const isAtInitialWithImages =
      history.length === 1 &&
      history[0]?.operationType === "initial" &&
      images &&
      images.length > 0;

    if (history.length <= 1 && !isAtInitialWithImages) {
      return;
    }

    // Special case: If we're at initial state with images, clear them (undo add_preset_image)
    if (isAtInitialWithImages) {
      setImages([]);
      isUndoingRedoing.current = false;
      return;
    }

    // Prevent recursive calls
    if (isUndoingRedoing.current) {
      return;
    }

    isUndoingRedoing.current = true;
    // Start grace period now so saveToHistory("update_image_properties") from deferred callbacks
    // (e.g. setTimeout(0) in mediaUtils) is skipped even before we clear the flag at 10ms
    if (lastUndoRedoCompletionTime) {
      lastUndoRedoCompletionTime.current = Date.now();
    }

    // Use snapshot passed at click time if provided (caller: () => undo(offCanvasImages)); else ref/closure
    const offCanvasAtUndoClick = Array.isArray(offCanvasSnapshotAtClick)
      ? offCanvasSnapshotAtClick
      : Array.isArray(offCanvasImagesRef?.current)
        ? [...offCanvasImagesRef.current]
        : Array.isArray(offCanvasImages)
          ? [...offCanvasImages]
          : [];

    try {
      // Find the first state that's different from current state
      // Skip identical states (e.g., from opening/closing text editor without changes)
      let targetStateIndex = history.length - 2; // Start with previous state
      let skippedCount = 0;
      const MAX_SKIPS = 50; // Safety limit to prevent infinite loops

      while (targetStateIndex >= 0 && skippedCount < MAX_SKIPS) {
        const currentState = history[history.length - 1];
        const candidateState = history[targetStateIndex];

        // DEBUG: Log each comparison
        const isEqual = areStatesEqual(currentState, candidateState);
        // CRITICAL: When undoing FROM a multi-delete, always go to the previous state.
        // The delete_multiple_objects entry has the same content as the state before it (state before deletion),
        // so areStatesEqual would skip it and we'd restore one step too far (only one image).
        if (currentState?.operationType === "delete_multiple_objects") {
          break;
        }

        const isDeleteOperation =
          candidateState?.operationType === "delete_object" ||
          candidateState?.operationType === "delete_multiple_objects";

        // CRITICAL: Never skip delete_object/delete_multiple_objects states - they represent important operations
        if (isDeleteOperation) {
          break; // Stop at delete, don't skip it
        }

        // CRITICAL: When undoing FROM an add_image operation, multiple add_image entries may exist
        // (e.g. from strict mode or merge logic). Skip consecutive add_image/add_image_clone states so
        // a single undo jumps back to the state before the image was first added.
        const isCurrentAddImageOperation =
          currentState?.operationType === "add_image" ||
          currentState?.operationType === "add_image_clone";
        const isCandidateAddImageOperation =
          candidateState?.operationType === "add_image" ||
          candidateState?.operationType === "add_image_clone";

        if (isCurrentAddImageOperation && isCandidateAddImageOperation) {
          skippedCount++;
          targetStateIndex--;
          continue;
        }

        // CRITICAL: When undoing FROM an edit operation, we need to find the state BEFORE the element was added
        // NOT stop at the add operation that created the element. Continue searching further back.
        const isCurrentEditOperation =
          currentState?.operationType === "element_edit_complete";
        const isCandidateAddOperation =
          candidateState?.operationType === "add_circle" ||
          candidateState?.operationType === "add_rectangle" ||
          candidateState?.operationType === "add_image" ||
          candidateState?.operationType === "add_text" ||
          candidateState?.operationType === "add_arrow" ||
          candidateState?.operationType === "add_line";

        if (isCurrentEditOperation && isCandidateAddOperation) {
          // CRITICAL FIX: For add_image/add_image_clone, treat the add + element_edit_complete
          // as a single logical operation so one undo removes the image.
          const isCandidateAddImage =
            candidateState?.operationType === "add_image" ||
            candidateState?.operationType === "add_image_clone";
          const isImmediatePreviousState =
            targetStateIndex === history.length - 2;

          // SPECIAL CASE: Off-canvas add_rectangle where history saved 0 main rectangles.
          // Pattern (from your logs):
          //   initial -> element_edit_complete (drag) -> add_rectangle (rectangles: 0, off-canvas only) -> element_edit_complete
          // When undoing from the last element_edit_complete, we want the FIRST undo to
          // remove the off-canvas rectangle (go back to the earlier element_edit_complete),
          // while preserving the drag. That means we must NOT stop on this add_rectangle
          // snapshot; instead, continue searching back past it.
          const isOffCanvasAddRectangleWithEmptyMain =
            candidateState?.operationType === "add_rectangle" &&
            Array.isArray(candidateState.rectangles) &&
            candidateState.rectangles.length === 0;

          // For add_text/add_text_clone: only skip when the edit was to the text just added.
          // If same text count AND there is an earlier add_text in history, the edit was to an
          // existing text — stop here so one undo only reverts the edit (not both texts).
          const currentTextsLen = (currentState.texts || []).length;
          const candidateTextsLen = (candidateState.texts || []).length;
          const isAddTextOrClone =
            candidateState?.operationType === "add_text" ||
            candidateState?.operationType === "add_text_clone";
          const sameTextCount = currentTextsLen === candidateTextsLen;
          // "Edited existing text" = add_text candidate, same count, and not the first add in history
          const editedExistingText =
            isAddTextOrClone &&
            sameTextCount &&
            targetStateIndex >= 2;

          // For non-image adds (rect/circle/etc.), stop at immediate previous state,
          // EXCEPT off-canvas add_rectangle-with-empty-main. For add_text: stop when
          // edit was to existing text (same count and earlier add_text exists); otherwise continue.
          if (
            isImmediatePreviousState &&
            !isCandidateAddImage &&
            !isOffCanvasAddRectangleWithEmptyMain &&
            (candidateState?.operationType !== "add_text" &&
              candidateState?.operationType !== "add_text_clone" ||
              editedExistingText)
          ) {
            break; // Stop at the immediate previous state (only undo the edit, or grouped non-text add)
          }

          // For add_image/add_image_clone (even when immediate previous), continue searching
          // so undo jumps directly to the state before the image was added.
          skippedCount++;
          targetStateIndex--;
          continue; // Skip this add operation and continue searching
        }

        // If states are different, use this state
        // CRITICAL: Don't give special treatment to delete_object in the search loop
        // Only the LAST state gets special treatment (handled separately above)
        // This ensures we find the most recent different state, not just any delete_object
        if (!isEqual) {
          // Found a different state - use it
          // Don't break immediately for delete_object - treat it like any other state
          break;
          // CRITICAL: For image, rectangle, and circle drag operations, we want to go back to the state before the drag
          // But we need to ensure we don't skip states that have important changes like rotation
          // If the current state is from a drag_end operation, we want to find the state before the drag
          // that has the same rotation as the current state (before drag, but with rotation preserved)
          if (candidateState && currentState) {
            const isCurrentImageDragEnd =
              currentState.operationType === "image_drag_end";
            const isCurrentRectangleDragEnd =
              currentState.operationType === "rectangle_drag_end";
            const isCurrentCircleDragEnd =
              currentState.operationType === "circle_drag_end";
            const isCurrentArrowDragEnd =
              currentState.operationType === "arrow_drag_end";
            const isCurrentDoubleSidedArrowDragEnd =
              currentState.operationType === "double_sided_arrow_drag_end";
            const isCurrentLineDragEnd =
              currentState.operationType === "line_drag_end" ||
              currentState.operationType === "line_drag_end_off_canvas";
            const isCurrentTextDragEnd =
              currentState.operationType === "text_drag_end";

            // Handle images
            if (isCurrentImageDragEnd) {
              const currentImage = currentState.images?.[0];
              const candidateImage = candidateState.images?.[0];

              // If both states have images with the same ID, and current is from drag_end
              if (currentImage?.id === candidateImage?.id) {
                const currentRotation = currentImage?.rotation ?? 0;
                const candidateRotation = candidateImage?.rotation ?? 0;

                // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                // we should look for a state between them that has the same rotation as current
                // This ensures we go back to before the drag (with rotation), not before the rotation
                if (
                  currentRotation !== candidateRotation &&
                  currentRotation !== 0
                ) {
                  // Look backwards from current state to find the most recent state with the same rotation
                  // This should be the state right before the drag (with rotation preserved)
                  for (let i = history.length - 2; i > targetStateIndex; i--) {
                    const intermediateState = history[i];
                    const intermediateImage = intermediateState?.images?.[0];
                    if (intermediateImage?.id === currentImage?.id) {
                      const intermediateRotation =
                        intermediateImage?.rotation ?? 0;
                      // If we find a state with the same rotation as current, use that instead
                      // This is the state before the drag, with rotation preserved
                      if (intermediateRotation === currentRotation) {
                        targetStateIndex = i;
                        break;
                      }
                    }
                  }
                }
              }
            }

            // Handle rectangles
            if (isCurrentRectangleDragEnd) {
              const currentRectangles = currentState.rectangles || [];
              const candidateRectangles = candidateState.rectangles || [];

              // Find rectangles that exist in both states and check their rotations
              for (const currentRect of currentRectangles) {
                const candidateRect = candidateRectangles.find(
                  (r) => r.id === currentRect.id,
                );

                if (candidateRect) {
                  const currentRotation = currentRect?.rotation ?? 0;
                  const candidateRotation = candidateRect?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateRectangles =
                        intermediateState?.rectangles || [];
                      const intermediateRectangle = intermediateRectangles.find(
                        (r) => r.id === currentRect.id,
                      );
                      if (intermediateRectangle) {
                        const intermediateRotation =
                          intermediateRectangle?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first rectangle with rotation mismatch (assuming only one rectangle is dragged at a time)
                    break;
                  }
                }
              }
            }

            // Handle circles
            if (isCurrentCircleDragEnd) {
              const currentCircles = currentState.circles || [];
              const candidateCircles = candidateState.circles || [];

              // Find circles that exist in both states and check their rotations
              for (const currentCircle of currentCircles) {
                const candidateCircle = candidateCircles.find(
                  (c) => c.id === currentCircle.id,
                );

                if (candidateCircle) {
                  const currentRotation = currentCircle?.rotation ?? 0;
                  const candidateRotation = candidateCircle?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateCircles =
                        intermediateState?.circles || [];
                      const intermediateCircle = intermediateCircles.find(
                        (c) => c.id === currentCircle.id,
                      );
                      if (intermediateCircle) {
                        const intermediateRotation =
                          intermediateCircle?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first circle with rotation mismatch (assuming only one circle is dragged at a time)
                    break;
                  }
                }
              }
            }

            // Handle arrows
            if (isCurrentArrowDragEnd) {
              const currentArrows = currentState.arrows || [];
              const candidateArrows = candidateState.arrows || [];

              // Find arrows that exist in both states and check their rotations
              for (const currentArrow of currentArrows) {
                const candidateArrow = candidateArrows.find(
                  (a) => a.id === currentArrow.id,
                );

                if (candidateArrow) {
                  const currentRotation = currentArrow?.rotation ?? 0;
                  const candidateRotation = candidateArrow?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateArrows =
                        intermediateState?.arrows || [];
                      const intermediateArrow = intermediateArrows.find(
                        (a) => a.id === currentArrow.id,
                      );
                      if (intermediateArrow) {
                        const intermediateRotation =
                          intermediateArrow?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first arrow with rotation mismatch (assuming only one arrow is dragged at a time)
                    break;
                  }
                }
              }
            }

            // Handle double-sided arrows
            if (isCurrentDoubleSidedArrowDragEnd) {
              const currentArrows = currentState.doubleSidedArrows || [];
              const candidateArrows = candidateState.doubleSidedArrows || [];

              // Find arrows that exist in both states and check their rotations
              for (const currentArrow of currentArrows) {
                const candidateArrow = candidateArrows.find(
                  (a) => a.id === currentArrow.id,
                );

                if (candidateArrow) {
                  const currentRotation = currentArrow?.rotation ?? 0;
                  const candidateRotation = candidateArrow?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateArrows =
                        intermediateState?.doubleSidedArrows || [];
                      const intermediateArrow = intermediateArrows.find(
                        (a) => a.id === currentArrow.id,
                      );
                      if (intermediateArrow) {
                        const intermediateRotation =
                          intermediateArrow?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first arrow with rotation mismatch (assuming only one arrow is dragged at a time)
                    break;
                  }
                }
              }
            }

            // Handle lines
            if (isCurrentLineDragEnd) {
              const currentLines = currentState.lines || [];
              const candidateLines = candidateState.lines || [];

              // Find lines that exist in both states and check their rotations
              for (const currentLine of currentLines) {
                const candidateLine = candidateLines.find(
                  (l) => l.id === currentLine.id,
                );

                if (candidateLine) {
                  const currentRotation = currentLine?.rotation ?? 0;
                  const candidateRotation = candidateLine?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateLines = intermediateState?.lines || [];
                      const intermediateLine = intermediateLines.find(
                        (l) => l.id === currentLine.id,
                      );
                      if (intermediateLine) {
                        const intermediateRotation =
                          intermediateLine?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first line with rotation mismatch (assuming only one line is dragged at a time)
                    break;
                  }
                }
              }
            }

            // Handle text
            if (isCurrentTextDragEnd) {
              const currentTexts = currentState.texts || [];
              const candidateTexts = candidateState.texts || [];

              // Find texts that exist in both states and check their rotations
              for (const currentText of currentTexts) {
                const candidateText = candidateTexts.find(
                  (t) => t.id === currentText.id,
                );

                if (candidateText) {
                  const currentRotation = currentText?.rotation ?? 0;
                  const candidateRotation = candidateText?.rotation ?? 0;

                  // If the current state (after drag) has rotation, but candidate state (before drag) has different rotation,
                  // we should look for a state between them that has the same rotation as current
                  // This ensures we go back to before the drag (with rotation), not before the rotation
                  if (
                    currentRotation !== candidateRotation &&
                    currentRotation !== 0
                  ) {
                    // Look backwards from current state to find the most recent state with the same rotation
                    // This should be the state right before the drag (with rotation preserved)
                    for (
                      let i = history.length - 2;
                      i > targetStateIndex;
                      i--
                    ) {
                      const intermediateState = history[i];
                      const intermediateTexts = intermediateState?.texts || [];
                      const intermediateText = intermediateTexts.find(
                        (t) => t.id === currentText.id,
                      );
                      if (intermediateText) {
                        const intermediateRotation =
                          intermediateText?.rotation ?? 0;
                        // If we find a state with the same rotation as current, use that instead
                        // This is the state before the drag, with rotation preserved
                        if (intermediateRotation === currentRotation) {
                          targetStateIndex = i;
                          break;
                        }
                      }
                    }
                    // Break after finding the first text with rotation mismatch (assuming only one text is dragged at a time)
                    break;
                  }
                }
              }
            }
          }
          break;
        }

        // States are the same, skip to next one
        skippedCount++;
        targetStateIndex--;
      }

      // If we've gone through all states or hit the limit, use the last available state
      if (targetStateIndex < 0) {
        targetStateIndex = 0;
      }

      const currentState = history[history.length - 1];

      const targetState = history[targetStateIndex];

      // DEBUG: Log target state selection with OFF-CANVAS details
      // Re-get targetState after potential adjustment (isLastStateDelete check above may have changed targetStateIndex)
      const finalTargetState = history[targetStateIndex];

      // DEBUG: Log ALL history states to see what's actually saved

      // Move all skipped states to redo stack
      const statesToMoveToRedo = history.slice(targetStateIndex + 1);

      // CRITICAL: If we're undoing from a cut operation, check if the current state (cut_image) has cropData
      // that should be preserved when restoring to an earlier state
      // Also check ALL states being moved to redo, as the cut might be in there
      const isCutOperation = currentState?.operationType === "cut_image";
      const cutState = isCutOperation ? currentState : null;

      // Check states being moved to redo (the cut_image should be in here)
      const cutStateInRedo = statesToMoveToRedo.find(
        (state) => state.operationType === "cut_image",
      );

      // Also check the entire history for cut_image operation (as a fallback)
      const cutStateInHistory = history.find(
        (state) => state.operationType === "cut_image",
      );

      const finalCutState = cutState || cutStateInRedo || cutStateInHistory;

      // Create a map of cut state images by ID for quick lookup
      const cutStateImagesMap = new Map();
      if (finalCutState?.images) {
        finalCutState.images.forEach((img) => {
          cutStateImagesMap.set(img.id, img);
        });
      }

      // Debug logging for cut state detection

      // DEBUG: Log undo operation with full history structure
      const targetImage = targetState?.images?.[0];
      const currentImage = currentState?.images?.[0];

      // Log all history states to understand the structure
      const historySummary = history.map((state, idx) => ({
        index: idx,
        operationType: state.operationType,
        imageRotation: state.images?.[0]?.rotation,
        imageScaleX: state.images?.[0]?.scaleX,
        imageScaleY: state.images?.[0]?.scaleY,
        imageX: state.images?.[0]?.x,
        imageY: state.images?.[0]?.y,
        imageId: state.images?.[0]?.id,
        // Log full image object to see all properties
        fullImage: state.images?.[0]
          ? {
            id: state.images[0].id,
            rotation: state.images[0].rotation,
            scaleX: state.images[0].scaleX,
            scaleY: state.images[0].scaleY,
            x: state.images[0].x,
            y: state.images[0].y,
            hasRotation: state.images[0].hasOwnProperty("rotation"),
            rotationType: typeof state.images[0].rotation,
          }
          : null,
      }));

      // Use targetState as the state to restore
      const previousState = targetState;
      const stateBeforeUndo = statesToMoveToRedo[0];

      // Compute off-canvas images restore value BEFORE flushSync. If we're undoing add_image to a state
      // that has empty offCanvasImages, set "current minus last" so it's applied in the same flushSync
      // as history update - otherwise a re-render from setHistory can overwrite offCanvasImages with [].
      const mainCanvasImageIdsForOffCanvas = new Set(
        (previousState.images || []).map((img) => img.id),
      );
      const prevOffCanvasForSync = Array.isArray(previousState.offCanvasImages)
        ? previousState.offCanvasImages
        : [];
      const isAddImageUndoToEmptySync =
        (previousState.operationType === "add_image" ||
          previousState.operationType === "add_image_clone") &&
        prevOffCanvasForSync.length === 0;
      // When history has empty offCanvasImages, use snapshot taken at undo click so "current minus last" is correct.
      // (Ref/closure can be overwritten during flushSync; offCanvasAtUndoClick is stable.)
      const currentFromStateBefore = stateBeforeUndo?.offCanvasImages;
      const currentOffCanvasForSync = isAddImageUndoToEmptySync
        ? offCanvasAtUndoClick
        : Array.isArray(currentFromStateBefore)
          ? currentFromStateBefore
          : Array.isArray(offCanvasImagesRef?.current)
            ? offCanvasImagesRef.current
            : Array.isArray(offCanvasImages)
              ? offCanvasImages
              : [];
      // When undoing from a main-canvas add (add_preset_image or add_image) to off-canvas-only state,
      // preserve current off-canvas image data (e.g. crop) so we don't restore uncropped from history.
      const isUndoFromMainAddToOffCanvasOnly =
        (stateBeforeUndo?.operationType === "add_preset_image" ||
          stateBeforeUndo?.operationType === "add_image") &&
        (previousState.operationType === "add_image" ||
          previousState.operationType === "add_image_clone") &&
        (previousState.images || []).length === 0 &&
        (stateBeforeUndo?.images || []).length > 0 &&
        (previousState.offCanvasImages || []).length > 0;

      // When undoing add_image that only added an off-canvas image, main canvas image IDs are unchanged.
      // Preserve current main canvas images (with rotation/transforms) instead of restoring from previousState.
      const prevMainIds = new Set(
        (previousState.images || []).map((img) => img.id),
      );
      const currentMainIds = new Set(
        (stateBeforeUndo?.images || []).map((img) => img.id),
      );
      const sameMainIds =
        prevMainIds.size === currentMainIds.size &&
        [...prevMainIds].every((id) => currentMainIds.has(id));
      const isUndoOffCanvasAddOnly =
        (previousState.operationType === "add_image" ||
          previousState.operationType === "add_image_clone") &&
        sameMainIds &&
        (stateBeforeUndo?.offCanvasImages?.length || 0) >
        (previousState.offCanvasImages?.length || 0);

      let restoredOffCanvasForSync;
      if (isAddImageUndoToEmptySync) {
        restoredOffCanvasForSync = currentOffCanvasForSync
          .slice(0, -1)
          .filter((img) => !mainCanvasImageIdsForOffCanvas.has(img.id));
      } else if (isUndoFromMainAddToOffCanvasOnly) {
        const prevOffCanvasList = (previousState.offCanvasImages || []).filter(
          (img) => !mainCanvasImageIdsForOffCanvas.has(img.id),
        );
        restoredOffCanvasForSync = prevOffCanvasList.map((histImg, index) => {
          // Match by id first; if id changed (e.g. after crop/replace), fall back to same index when lengths match
          let current = offCanvasAtUndoClick.find((c) => c.id === histImg.id);
          if (
            !current &&
            prevOffCanvasList.length === offCanvasAtUndoClick.length &&
            offCanvasAtUndoClick[index]
          ) {
            current = offCanvasAtUndoClick[index];
          }
          return current
            ? {
              ...histImg,
              ...current,
              image: current.image ?? histImg.image,
              originalImage: current.originalImage ?? histImg.originalImage,
              compressedImage:
                current.compressedImage ?? histImg.compressedImage,
            }
            : histImg;
        });
      } else {
        restoredOffCanvasForSync = prevOffCanvasForSync.filter(
          (img) => !mainCanvasImageIdsForOffCanvas.has(img.id),
        );
      }

      // CRITICAL: History snapshots are JSON-serialized so they have no .image (HTMLImageElement).
      // ImageElement needs img.image to render. Merge with offCanvasAtUndoClick to preserve .image for kept images.
      const mergedOffCanvasForSync = restoredOffCanvasForSync.map(
        (historyImg) => {
          const currentImg = offCanvasAtUndoClick.find(
            (img) => img.id === historyImg.id,
          );
          if (currentImg && (currentImg.image || currentImg.originalImage)) {
            return {
              ...historyImg,
              image: currentImg.image ?? historyImg.image,
              originalImage:
                currentImg.originalImage ?? historyImg.originalImage,
              compressedImage:
                currentImg.compressedImage ?? historyImg.compressedImage,
            };
          }
          return historyImg;
        },
      );

      if (
        previousState.operationType === "add_image" ||
        previousState.operationType === "add_image_clone"
      ) {
        const snapLen = Array.isArray(offCanvasSnapshotAtClick)
          ? offCanvasSnapshotAtClick.length
          : null;
        const prevLen = prevOffCanvasForSync.length;
        const beforeLen = Array.isArray(stateBeforeUndo?.offCanvasImages)
          ? stateBeforeUndo.offCanvasImages.length
          : null;
        const refLen = Array.isArray(offCanvasImagesRef?.current)
          ? offCanvasImagesRef.current.length
          : null;
        const closureLen = Array.isArray(offCanvasImages)
          ? offCanvasImages.length
          : null;
      }

      // Move skipped states to redo stack and update history.
      // CRITICAL: Also set offCanvasImages in this same flushSync so the re-render from setHistory
      // doesn't leave off-canvas empty (e.g. when last history entry has offCanvasImages: []).
      flushSync(() => {
        setRedoStack((prev) => {
          const newStack = [...statesToMoveToRedo, ...prev];
          return newStack;
        });
        setHistory((prev) => prev.slice(0, targetStateIndex + 1));
        setOffCanvasImages(mergedOffCanvasForSync);
      });

      // Restore all canvas elements atomically - optimized for speed
      const restoreCanvasState = () => {
        // Helper function to normalize strokeColor (convert number to hex string if needed)
        const normalizeStrokeColor = (color) => {
          if (!color) return "#000000";
          if (typeof color === "string" && color.startsWith("#")) return color;
          if (typeof color === "string") return color; // Already a string
          if (typeof color === "number") {
            return `#${color.toString(16).padStart(6, "0")}`;
          }
          return "#000000";
        };

        // Baseline preservation: ensure API/baseline elements from the initial
        // history entry are not lost when undoing non-destructive operations.
        const initialState = history.length > 0 ? history[0] : null;
        const baselineRectangles = Array.isArray(initialState?.rectangles)
          ? initialState.rectangles
          : [];
        const baselineTexts = Array.isArray(initialState?.texts)
          ? initialState.texts
          : [];
        const isDestructiveOperation =
          previousState.operationType === "delete_object" ||
          previousState.operationType === "delete_multiple_objects" ||
          previousState.operationType === "clear_canvas";

        const applyBaselineRectangles = (rects) => {
          if (isDestructiveOperation || baselineRectangles.length === 0) {
            return rects;
          }
          const existingIds = new Set((rects || []).map((r) => r.id));
          const merged = [
            ...(Array.isArray(rects) ? rects : []),
            ...baselineRectangles.filter(
              (r) => r && !existingIds.has(r.id),
            ),
          ];
          return merged;
        };

        const applyBaselineTexts = (texts) => {
          if (isDestructiveOperation || baselineTexts.length === 0) {
            return texts;
          }
          const existingIds = new Set((texts || []).map((t) => t.id));
          const merged = [
            ...(Array.isArray(texts) ? texts : []),
            ...baselineTexts.filter(
              (t) => t && !existingIds.has(t.id),
            ),
          ];
          return merged;
        };

        // Batch all state updates together for instant restoration
        // React 18+ automatically batches these updates
        const restoredArrows = (previousState.arrows || []).map((arrow) => {
          // Normalize strokeColor to ensure it's always a string
          const normalizedStroke = normalizeStrokeColor(
            arrow.stroke || arrow.strokeColor,
          );
          return {
            ...arrow,
            stroke: normalizedStroke,
            strokeColor: normalizedStroke,
            angle: arrow.angle ?? 90, // Ensure angle is always present
          };
        });

        // Clear all selections first
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Restore all elements synchronously - batched together
        // When undoing to add_arrow that has 0 main arrows (off-canvas add only), leave main canvas arrows unchanged.
        const isOffCanvasAddArrowPreserve =
          (previousState.operationType === "add_arrow" ||
            previousState.operationType === "add_arrow_clone") &&
          previousState.arrows?.length === 0;
        if (!isOffCanvasAddArrowPreserve) {
          setArrows(restoredArrows);
        }

        // Ensure all lines have startDot and endDot properties when restored
        const restoredLines = (previousState.lines || []).map((line) => {
          // Ensure line has the required dot properties
          const lineWithDots = {
            ...line,
            startDot: line.startDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
            endDot: line.endDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
          };
          return lineWithDots;
        });

        // When undoing to add_line that has 0 main lines, leave main canvas lines unchanged.
        // That state was created by adding a line on off-canvas only; main canvas (e.g. API lines) should not be cleared.
        // CRITICAL FIX: Only preserve if no main lines AND no off-canvas lines in previous state
        const isOffCanvasAddLinePreserve =
          (previousState.operationType === "add_line" ||
            previousState.operationType === "add_line_clone") &&
          previousState.lines?.length === 0 &&
          (previousState.offCanvasLines || []).length === 0; // Only preserve if no main lines AND no off-canvas lines

        if (!isOffCanvasAddLinePreserve) {
          setLines(restoredLines);
        }

        // Ensure restored shapes are not treated as 'new' and have full opacity defaults
        // CRITICAL: Filter out any rectangles that are in offCanvasRectangles to prevent duplicates
        // This ensures rectangles created in off-canvas don't appear in main canvas on undo
        // CRITICAL FIX: For add_rectangle states that saved main rectangles as an empty array (off-canvas-only adds),
        // fall back to the last earlier history state that had main rectangles so undo doesn't clear them.
        let baseRectanglesSource = previousState.rectangles || [];
        if (
          baseRectanglesSource.length === 0 &&
          (previousState.operationType === "add_rectangle" ||
            previousState.operationType === "add_rectangle_clone") &&
          Array.isArray(history) &&
          history.length > 0
        ) {
          for (let i = targetStateIndex - 1; i >= 0; i--) {
            const candidate = history[i];
            if (
              candidate &&
              Array.isArray(candidate.rectangles) &&
              candidate.rectangles.length > 0
            ) {
              baseRectanglesSource = candidate.rectangles;
              break;
            }
          }
        }
        const offCanvasRectIds = new Set(
          (previousState.offCanvasRectangles || []).map((r) => r.id),
        );
        // Also treat any rectangles that are off-canvas in the state we're undoing FROM
        // as off-canvas here, so they don't get re-applied to the main canvas when history
        // accidentally stored them under `rectangles` instead of `offCanvasRectangles`.
        (stateBeforeUndo?.offCanvasRectangles || []).forEach((r) =>
          offCanvasRectIds.add(r.id),
        );

        const restoredRectangles = (baseRectanglesSource || [])
          .filter((rect) => !offCanvasRectIds.has(rect.id)) // Exclude off-canvas rectangles
          .map((rect) => ({
            ...rect, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: rect.fillOpacity ?? 1,
            strokeOpacity: rect.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: rect.text ?? "",
            textColor: rect.textColor,
            align: rect.align ?? "center",
            verticalAlign: rect.verticalAlign ?? "middle",
            fontSize: rect.fontSize,
            fontStyle: rect.fontStyle,
            textDecoration: rect.textDecoration,
          }));

        // CRITICAL: Preserve rectangles that exist in current state but not in previous state
        // This handles the case where a rectangle was added AFTER the operation being undone
        // ONLY preserve when undoing delete operations, not when undoing add operations
        const currentRectIds = new Set(rectangles.map((r) => r.id));
        const previousRectIds = new Set(restoredRectangles.map((r) => r.id));
        const newRects = rectangles.filter(
          (r) => !previousRectIds.has(r.id) && currentRectIds.has(r.id),
        );

        // Only preserve new rectangles if:
        // 1. The operation being undone is a DELETE operation (not an add operation)
        // 2. The operation being undone is NOT a rectangle operation
        // 3. We're NOT undoing to the initial state
        const isRectangleOperation =
          previousState.operationType?.includes("rectangle");
        const isUndoingToInitial = previousState.operationType === "initial";
        const isRectDeleteOperation =
          previousState.operationType === "delete_object";
        const shouldPreserveRectangles =
          newRects.length > 0 &&
          isRectDeleteOperation &&
          !isRectangleOperation &&
          !isUndoingToInitial;

        // When undoing to add_rectangle (off-canvas), history often stores the off-canvas rect in "rectangles" (wrong)
        // and offCanvasRectangles is empty (see log: rectangleIds: ['marker-*'], rectanglesCount: 1).
        // Only treat as "wrong storage" when the rect(s) from add_rectangle are actually on off-canvas in the
        // state we're undoing FROM — i.e. the add was done on off-canvas. If the rect was on main in stateBeforeUndo,
        // the add was on main and we must restore previousState.rectangles to main and leave offCanvas empty.
        const prevRectIdsFromAdd = new Set(
          (baseRectanglesSource || []).map((r) => r.id),
        );
        const beforeOffRectIds = new Set(
          (stateBeforeUndo?.offCanvasRectangles || []).map((r) => r.id),
        );
        const addRectRectsWereOnOffCanvasBeforeUndo =
          prevRectIdsFromAdd.size > 0 &&
          [...prevRectIdsFromAdd].some((id) => beforeOffRectIds.has(id));
        const isAddRectOffCanvasStoredInMain =
          (previousState.operationType === "add_rectangle" ||
            previousState.operationType === "add_rectangle_clone") &&
          (previousState.offCanvasRectangles || []).length === 0 &&
          (previousState.rectangles || []).length > 0 &&
          addRectRectsWereOnOffCanvasBeforeUndo;

        // CRITICAL FIX: isOffCanvasAddRectanglePreserve should only be true when the rectangle was actually
        // added to off-canvas (not when it was added to main canvas). Check both the target state AND
        // the state we're undoing FROM to determine if this was truly an off-canvas addition.
        const currentStateHasMainRects =
          (stateBeforeUndo?.rectangles?.length || 0) > 0;
        const currentStateHasOffCanvasRects =
          (stateBeforeUndo?.offCanvasRectangles?.length || 0) > 0;
        const targetStateHasMainRects =
          (previousState?.rectangles?.length || 0) > 0;
        const targetStateHasOffCanvasRects =
          (previousState?.offCanvasRectangles?.length || 0) > 0;

        // This is an off-canvas addition preserve case ONLY if:
        // 1. We're undoing an add_rectangle operation
        // 2. The target state has no main rectangles (we're going back to before the main rectangle was added)
        // 3. CRITICAL: The rectangles from add_rectangle were actually added to off-canvas (not main)
        // 4. The current state we're undoing FROM must have main rectangles (otherwise we're not removing a main rectangle)
        const isOffCanvasAddRectanglePreserve =
          (previousState.operationType === "add_rectangle" ||
            previousState.operationType === "add_rectangle_clone") &&
          !targetStateHasMainRects && // Target state has no main rectangles
          addRectRectsWereOnOffCanvasBeforeUndo && // Rectangles were actually added off-canvas
          currentStateHasMainRects; // Current state has main rectangles (so we're actually removing something)

        if (isOffCanvasAddRectanglePreserve) {
          // When off-canvas rect was stored in main: main = stateBeforeUndo.rectangles only; else use restored/current.
          const mainRectsToUse = isAddRectOffCanvasStoredInMain
            ? stateBeforeUndo?.rectangles || []
            : stateBeforeUndo?.rectangles?.length > 0 ||
              restoredRectangles.length > 0
              ? restoredRectangles.length > 0
                ? restoredRectangles
                : stateBeforeUndo?.rectangles || rectangles
              : rectangles;
          setRectangles(
            applyBaselineRectangles(
              mainRectsToUse.map((rect) => ({
                ...rect,
                isNew: false,
                fillOpacity: rect.fillOpacity ?? 1,
                strokeOpacity: rect.strokeOpacity ?? 1,
                opacity: 1,
                text: rect.text ?? "",
                textColor: rect.textColor,
                align: rect.align ?? "center",
                verticalAlign: rect.verticalAlign ?? "middle",
                fontSize: rect.fontSize,
                fontStyle: rect.fontStyle,
                textDecoration: rect.textDecoration,
              })),
            ),
          );
        } else if (shouldPreserveRectangles) {
          console.warn(
            "⚠️ [UNDO] Preserving rectangles that were added after previousState",
            {
              operationType: previousState.operationType,
              preservedRectIds: newRects.map((r) => r.id.substring(0, 20)),
              prevRectCount: restoredRectangles.length,
              newRectCount: newRects.length,
              totalRectCount: restoredRectangles.length + newRects.length,
            },
          );

          // Merge previous rectangles with new rectangles
          const mergedRectangles = [
            ...restoredRectangles,
            ...newRects.map((rect) => ({
              ...rect,
              isNew: false,
              fillOpacity: rect.fillOpacity ?? 1,
              strokeOpacity: rect.strokeOpacity ?? 1,
              opacity: 1,
              text: rect.text ?? "",
              textColor: rect.textColor,
              align: rect.align ?? "center",
              verticalAlign: rect.verticalAlign ?? "middle",
              fontSize: rect.fontSize,
              fontStyle: rect.fontStyle,
              textDecoration: rect.textDecoration,
            })),
          ];
          setRectangles(applyBaselineRectangles(mergedRectangles));
        } else {
          setRectangles(applyBaselineRectangles(restoredRectangles));
        }

        // SPECIAL CASE FIX (revised): When history is
        // [initial, add_rectangle (off-canvas snapshot mis-stored in `rectangles`), add_rectangle (main)]
        // and we undo from the top, the off-canvas rectangle must NOT be brought onto the main canvas.
        //
        // IMPORTANT: This MUST NOT trigger for the common case where the user added multiple rectangles
        // on off-canvas only (your reported bug), otherwise the first undo becomes a no-op and the second
        // undo clears everything.
        //
        // So we only apply this when:
        // - the *target* (previousState) has rects in `rectangles` but NONE in `offCanvasRectangles` (mis-storage)
        // - the state we're undoing FROM has BOTH main rectangles and off-canvas rectangles
        // - and the mis-stored ids match ids present in the off-canvas list (proves they are off-canvas rects)
        const isThreeStateOffCanvasLeakFix = (() => {
          if (
            !Array.isArray(history) ||
            history.length !== 3 ||
            history[0]?.operationType !== "initial"
          ) {
            return false;
          }

          const isPrevAddRect =
            previousState.operationType === "add_rectangle" ||
            previousState.operationType === "add_rectangle_clone";
          const isBeforeAddRect =
            stateBeforeUndo?.operationType === "add_rectangle" ||
            stateBeforeUndo?.operationType === "add_rectangle_clone";
          if (!isPrevAddRect || !isBeforeAddRect) return false;

          const prevMain = Array.isArray(previousState.rectangles)
            ? previousState.rectangles
            : [];
          const prevOff = Array.isArray(previousState.offCanvasRectangles)
            ? previousState.offCanvasRectangles
            : [];
          const beforeMain = Array.isArray(stateBeforeUndo?.rectangles)
            ? stateBeforeUndo.rectangles
            : [];
          const beforeOff = Array.isArray(stateBeforeUndo?.offCanvasRectangles)
            ? stateBeforeUndo.offCanvasRectangles
            : [];

          // Require the mis-storage shape: rects in main, none in off-canvas
          if (prevMain.length === 0 || prevOff.length > 0) return false;
          // Require we're undoing FROM a state that has both storages populated (main add + off-canvas already existed)
          if (beforeMain.length === 0 || beforeOff.length === 0) return false;

          const beforeOffIds = new Set(beforeOff.map((r) => r.id));
          const prevMainIds = prevMain.map((r) => r.id);
          const hasIdIntersection = prevMainIds.some((id) => beforeOffIds.has(id));
          return hasIdIntersection;
        })();

        if (isThreeStateOffCanvasLeakFix) {
          const baselineRects = Array.isArray(history[0].rectangles)
            ? history[0].rectangles
            : [];

          // Restore ONLY the baseline (initial) main rectangles here. Any rectangles that
          // existed only in the intermediate add_rectangle snapshot are treated as
          // off-canvas-only and must not be injected into main.
          const baselineRestored = baselineRects.map((rect) => ({
            ...rect,
            isNew: false,
            fillOpacity: rect.fillOpacity ?? 1,
            strokeOpacity: rect.strokeOpacity ?? 1,
            opacity: 1,
            text: rect.text ?? "",
            textColor: rect.textColor,
            align: rect.align ?? "center",
            verticalAlign: rect.verticalAlign ?? "middle",
            fontSize: rect.fontSize,
            fontStyle: rect.fontStyle,
            textDecoration: rect.textDecoration,
          }));

          // Main canvas: restore to initial rectangles only.
          setRectangles(baselineRestored);

          // Off-canvas: explicitly preserve whatever off-canvas rectangles existed
          // in the state we're undoing FROM (which contains the off-canvas-only rect
          // from the first add_rectangle). History often doesn't store this correctly,
          // so we force it here to avoid losing the off-canvas rect.
          if (Array.isArray(stateBeforeUndo?.offCanvasRectangles)) {
            const preservedOffCanvasRects =
              stateBeforeUndo.offCanvasRectangles.map((rect) => ({
                ...rect,
                isNew: false,
                fillOpacity: rect.fillOpacity ?? 1,
                strokeOpacity: rect.strokeOpacity ?? 1,
                opacity: 1,
                text: rect.text ?? "",
                textColor: rect.textColor,
                align: rect.align ?? "center",
                verticalAlign: rect.verticalAlign ?? "middle",
                fontSize: rect.fontSize,
                fontStyle: rect.fontStyle,
                textDecoration: rect.textDecoration,
              }));
            setOffCanvasRectangles(preservedOffCanvasRects);
          }

          // IMPORTANT: We've fully handled the special 3-state pattern here.
          // Short-circuit the rest of the generic restore logic so it doesn't
          // override our preserved off-canvas rectangles or baseline main rectangles.
          return;
        }

        // CRITICAL: Filter out any circles that are in offCanvasCircles to prevent duplicates
        // This ensures circles created in off-canvas don't appear in main canvas on undo
        const offCanvasCircleIds = new Set(
          (previousState.offCanvasCircles || []).map((c) => c.id),
        );
        const restoredCircles = (finalTargetState.circles || [])
          .filter((circle) => !offCanvasCircleIds.has(circle.id)) // Exclude off-canvas circles
          .map((circle) => ({
            ...circle, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: circle.fillOpacity ?? 1,
            strokeOpacity: circle.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: circle.text ?? "",
            textColor: circle.textColor,
            align: circle.align ?? "center",
            verticalAlign: circle.verticalAlign ?? "middle",
            fontSize: circle.fontSize,
            fontStyle: circle.fontStyle,
            textDecoration: circle.textDecoration,
          }));

        // CRITICAL: Preserve circles that exist in current state but not in previous state
        // This handles the case where a circle was added AFTER the operation being undone
        // ONLY preserve when undoing delete operations, not when undoing add operations
        // This ensures that undoing "add_circle" will actually remove the circle
        const currentCircleIds = new Set(circles.map((c) => c.id));
        const previousCircleIds = new Set(restoredCircles.map((c) => c.id));
        const newCircles = circles.filter(
          (c) => !previousCircleIds.has(c.id) && currentCircleIds.has(c.id),
        );

        // Only preserve new circles if:
        // 1. The operation being undone is a DELETE operation (not an add operation)
        // 2. The operation being undone is NOT a circle operation
        // 3. We're NOT undoing to the initial state
        const isCircleOperation =
          previousState.operationType?.includes("circle");
        const isInitialState = previousState.operationType === "initial";
        const isDeleteOperation =
          previousState.operationType === "delete_object";
        const shouldPreserveCircles =
          newCircles.length > 0 &&
          isDeleteOperation &&
          !isCircleOperation &&
          !isInitialState;

        // When undoing to add_circle (off-canvas add), leave main canvas circles unchanged if either:
        // (1) previous state has 0 main circles, or
        // (2) after excluding off-canvas circles we get 0 (history may have stored off-canvas circle in "circles")
        //     and current state has main circles (e.g. API circles) to preserve.
        // Only treat as off-canvas add when the circle(s) from add_circle are actually on off-canvas in the
        // state we're undoing FROM — otherwise restore previousState.circles to main (add was on main).
        const prevCircleIdsFromAdd = new Set(
          (previousState.circles || []).map((c) => c.id),
        );
        const beforeOffCircleIds = new Set(
          (stateBeforeUndo?.offCanvasCircles || []).map((c) => c.id),
        );
        const addCircleCirclesWereOnOffCanvasBeforeUndo =
          prevCircleIdsFromAdd.size > 0 &&
          [...prevCircleIdsFromAdd].some((id) => beforeOffCircleIds.has(id));

        // CRITICAL FIX: isOffCanvasAddCirclePreserve should only be true when the circle was actually
        // added to off-canvas (not when it was added to main canvas). Check both the target state AND
        // the state we're undoing FROM to determine if this was truly an off-canvas addition.
        const currentStateHasMainCircles =
          (stateBeforeUndo?.circles?.length || 0) > 0;
        const currentStateHasOffCanvasCircles =
          (stateBeforeUndo?.offCanvasCircles?.length || 0) > 0;
        const targetStateHasMainCircles =
          (finalTargetState?.circles?.length || 0) > 0;
        const targetStateHasOffCanvasCircles =
          (finalTargetState?.offCanvasCircles?.length || 0) > 0;

        // This is an off-canvas addition preserve case ONLY if:
        // 1. We're undoing an add_circle operation
        // 2. The target state has no main circles (we're going back to before the main circle was added)
        // 3. CRITICAL: The circles from add_circle were actually added to off-canvas (not main)
        // 4. The current state we're undoing FROM must have main circles (otherwise we're not removing a main circle)
        const isOffCanvasAddCirclePreserve =
          (previousState.operationType === "add_circle" ||
            previousState.operationType === "add_circle_clone") &&
          !targetStateHasMainCircles && // Target state has no main circles
          addCircleCirclesWereOnOffCanvasBeforeUndo && // Circles were actually added off-canvas
          currentStateHasMainCircles; // Current state has main circles (so we're actually removing something)

        if (isOffCanvasAddCirclePreserve) {
          // Main: use state we're undoing from (correct main circles e.g. API); avoid leaving current which can include later-added main circles.
          const mainCirclesToUse =
            stateBeforeUndo?.circles?.length > 0 || restoredCircles.length > 0
              ? restoredCircles.length > 0
                ? restoredCircles
                : stateBeforeUndo?.circles || circles
              : circles;
          setCircles(
            mainCirclesToUse.map((circle) => ({
              ...circle,
              isNew: false,
              fillOpacity: circle.fillOpacity ?? 1,
              strokeOpacity: circle.strokeOpacity ?? 1,
              opacity: 1,
              text: circle.text ?? "",
              textColor: circle.textColor,
              align: circle.align ?? "center",
              verticalAlign: circle.verticalAlign ?? "middle",
              fontSize: circle.fontSize,
              fontStyle: circle.fontStyle,
              textDecoration: circle.textDecoration,
            })),
          );
        } else if (shouldPreserveCircles) {
          console.warn(
            "⚠️ [UNDO] Preserving circles that were added after previousState",
            {
              operationType: previousState.operationType,
              preservedCircleIds: newCircles.map((c) => c.id.substring(0, 20)),
              prevCircleCount: restoredCircles.length,
              newCircleCount: newCircles.length,
              totalCircleCount: restoredCircles.length + newCircles.length,
            },
          );

          // Merge previous circles with new circles
          const mergedCircles = [
            ...restoredCircles,
            ...newCircles.map((circle) => ({
              ...circle,
              isNew: false,
              fillOpacity: circle.fillOpacity ?? 1,
              strokeOpacity: circle.strokeOpacity ?? 1,
              opacity: 1,
              text: circle.text ?? "",
              textColor: circle.textColor,
              align: circle.align ?? "center",
              verticalAlign: circle.verticalAlign ?? "middle",
              fontSize: circle.fontSize,
              fontStyle: circle.fontStyle,
              textDecoration: circle.textDecoration,
            })),
          ];
          setCircles(mergedCircles);
        } else {
          setCircles(restoredCircles);
        }

        const restoredDoubleSidedArrowsUndo = (
          previousState.doubleSidedArrows || []
        ).map((arrow) => {
          // Normalize strokeColor to ensure it's always a string
          const normalizedStroke = normalizeStrokeColor(
            arrow.stroke || arrow.strokeColor,
          );
          return {
            ...arrow,
            stroke: normalizedStroke,
            strokeColor: normalizedStroke,
            angle: arrow.angle ?? 90, // Ensure angle is always present
          };
        });
        // When undoing to add_double_sided_arrow that has 0 main (off-canvas add only), leave main canvas doubleSidedArrows unchanged.
        const isOffCanvasAddDoubleSidedArrowPreserve =
          (previousState.operationType === "add_double_sided_arrow" ||
            previousState.operationType === "add_double_sided_arrow_clone") &&
          previousState.doubleSidedArrows?.length === 0;
        if (!isOffCanvasAddDoubleSidedArrowPreserve) {
          setDoubleSidedArrows(restoredDoubleSidedArrowsUndo);
        }

        // Restore texts. In most cases we can take them directly from previousState,
        // but if previousState has no texts while the state we're undoing from does,
        // we must not wipe main-canvas/API texts. That pattern typically indicates
        // an off-canvas-only operation where texts in previousState were never
        // captured correctly.
        const previousTexts = Array.isArray(previousState.texts)
          ? previousState.texts
          : [];
        const stateBeforeTexts = Array.isArray(stateBeforeUndo?.texts)
          ? stateBeforeUndo.texts
          : [];

        // CRITICAL FIX: Only preserve main texts for off-canvas operations, not for add_text operations
        // When undoing add_text back to initial state, we MUST clear the texts
        const isAddTextOperation =
          stateBeforeUndo?.operationType === "add_text" ||
          currentState?.operationType === "add_text";
        const shouldPreserveMainTexts =
          previousTexts.length === 0 &&
          stateBeforeTexts.length > 0 &&
          !isAddTextOperation; // Don't preserve when undoing add_text

        if (!shouldPreserveMainTexts) {
          setTexts(applyBaselineTexts(previousTexts));
        } else {
          // When preserving, still apply baseline texts to ensure API/baseline texts aren't lost
          setTexts(applyBaselineTexts(stateBeforeTexts));
        }
        setVideos(previousState.videos || []); // Restore videos
        setScribbles(previousState.scribbles || []);

        // Restore offcanvas elements
        // CRITICAL: Filter out any rectangles that are in main canvas rectangles to prevent duplicates
        // This ensures rectangles created in main canvas don't appear in off-canvas on undo
        const mainCanvasRectIds = new Set(
          (previousState.rectangles || []).map((r) => r.id),
        );
        // When undoing to add_rectangle (off-canvas), history may have saved the off-canvas rect in "rectangles" (wrong array).
        // Use previousState.rectangles as off-canvas source in that case; else stateBeforeUndo/current when offCanvasRectangles empty.
        // CRITICAL FIX: Check the state we're undoing FROM, not the state we're undoing TO
        const stateBeforeUndoOperationType = stateBeforeUndo?.operationType;
        const isAddRectOffCanvasWithEmptyStorage =
          (stateBeforeUndoOperationType === "add_rectangle" ||
            stateBeforeUndoOperationType === "add_rectangle_clone") &&
          (previousState.offCanvasRectangles || []).length === 0 &&
          // ADDITIONAL CHECK: Only treat as off-canvas if we're NOT undoing to initial state
          // When undoing add_rectangle back to initial, the rectangle should be removed from main canvas, not moved to off-canvas
          previousState.operationType !== "initial";
        const offCanvasRectsSource = isAddRectOffCanvasStoredInMain
          ? previousState.rectangles || []
          : isAddRectOffCanvasWithEmptyStorage
            ? stateBeforeUndo?.offCanvasRectangles?.length
              ? stateBeforeUndo.offCanvasRectangles
              : offCanvasRectangles || []
            : previousState.offCanvasRectangles || [];

        const restoredOffCanvasRectangles = offCanvasRectsSource
          .filter(
            (rect) =>
              isAddRectOffCanvasStoredInMain || !mainCanvasRectIds.has(rect.id),
          ) // When wrong storage, keep all; else exclude main canvas rectangles
          .map((rect) => ({
            ...rect, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: rect.fillOpacity ?? 1,
            strokeOpacity: rect.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: rect.text ?? "",
            textColor: rect.textColor,
            align: rect.align ?? "center",
            verticalAlign: rect.verticalAlign ?? "middle",
            fontSize: rect.fontSize,
            fontStyle: rect.fontStyle,
            textDecoration: rect.textDecoration,
          }));
        setOffCanvasTexts(previousState.offCanvasTexts || []);

        setOffCanvasRectangles(restoredOffCanvasRectangles);
        // CRITICAL: Filter out any circles that are in main canvas circles to prevent duplicates
        // This ensures circles created in main canvas don't appear in off-canvas on undo
        const mainCanvasCircleIds = new Set(
          (previousState.circles || []).map((c) => c.id),
        );
        // When undoing to add_circle (off-canvas), history may have saved offCanvasCircles as empty.
        // Use state we're undoing from first, then current offCanvasCircles so existing off-canvas circles are preserved.
        // CRITICAL FIX: Check the state we're undoing FROM, not the state we're undoing TO
        const isAddCircleOffCanvasWithEmptyStorage =
          (stateBeforeUndoOperationType === "add_circle" ||
            stateBeforeUndoOperationType === "add_circle_clone") &&
          (previousState.offCanvasCircles || []).length === 0 &&
          // ADDITIONAL CHECK: Only treat as off-canvas if we're NOT undoing to initial state
          // When undoing add_circle back to initial, the circle should be removed from main canvas, not moved to off-canvas
          previousState.operationType !== "initial" &&
          // NEW CHECK: Don't apply this preservation path when the target state itself
          // is an explicit add_* operation (e.g. add_rectangle). In that case, we are
          // undoing only the last add_circle step and should drop the off-canvas circle,
          // reverting to the earlier state (which legitimately has no offCanvasCircles).
          !(previousState.operationType || "").startsWith("add_");
        const offCanvasCirclesSource = isAddCircleOffCanvasWithEmptyStorage
          ? stateBeforeUndo?.offCanvasCircles?.length
            ? stateBeforeUndo.offCanvasCircles
            : offCanvasCircles || []
          : previousState.offCanvasCircles || [];

        
        const restoredOffCanvasCircles = offCanvasCirclesSource
          .filter((circle) => !mainCanvasCircleIds.has(circle.id)) // Exclude main canvas circles
          .map((circle) => ({
            ...circle, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: circle.fillOpacity ?? 1,
            strokeOpacity: circle.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: circle.text ?? "",
            textColor: circle.textColor,
            align: circle.align ?? "center",
            verticalAlign: circle.verticalAlign ?? "middle",
            fontSize: circle.fontSize,
            fontStyle: circle.fontStyle,
            textDecoration: circle.textDecoration,
          }));
        setOffCanvasCircles(restoredOffCanvasCircles);
        setOffCanvasArrows(previousState.offCanvasArrows || []);
        setOffCanvasDoubleSidedArrows(
          previousState.offCanvasDoubleSidedArrows || [],
        );
        // CRITICAL: Filter out any images that are in main canvas images to prevent duplicates
        // This ensures images created in main canvas don't appear in off-canvas on undo
        const mainCanvasImageIds = new Set(
          (previousState.images || []).map((img) => img.id),
        );
        const prevOffCanvas = Array.isArray(previousState.offCanvasImages)
          ? previousState.offCanvasImages
          : [];
        const isAddImageUndoToEmpty =
          (previousState.operationType === "add_image" ||
            previousState.operationType === "add_image_clone") &&
          prevOffCanvas.length === 0;

        if (
          previousState.operationType === "add_image" ||
          previousState.operationType === "add_image_clone"
        ) {
        }

        // Use same value we set in flushSync (mergedOffCanvasForSync) so .image is preserved and UI renders.
        // When restoring from history, image objects may lack .image (JSON-serialized). Load from imageDataUrl/src so they render.
        const setOffCanvasWithLoadedImages = (imagesToSet) => {
          const needsLoad = imagesToSet.filter(
            (img) => !img.image && (img.imageDataUrl || img.src),
          );
          if (needsLoad.length === 0) {
            setOffCanvasImages(imagesToSet);
            return;
          }
          Promise.all(
            needsLoad.map((img) =>
              createImageLoadPromise({ imgData: img, info: img }),
            ),
          )
            .then((loadedArray) => {
              const loadedById = new Map(
                (loadedArray || [])
                  .filter((r) => r && r.id)
                  .map((r) => [r.id, r]),
              );
              const withImages = imagesToSet.map((hist) => {
                const loaded = loadedById.get(hist.id);
                if (loaded && loaded.image) {
                  return {
                    ...hist,
                    image: loaded.image,
                    originalImage: loaded.originalImage || loaded.image,
                    compressedImage: loaded.compressedImage,
                  };
                }
                return hist;
              });
              setOffCanvasImages(withImages);
            })
            .catch((err) => {
              console.error(
                "❌ [UNDO] Error loading off-canvas images for restore:",
                err,
              );
              setOffCanvasImages(imagesToSet);
            });
        };

        if (isAddImageUndoToEmpty) {
          setOffCanvasWithLoadedImages(mergedOffCanvasForSync);
        } else if (
          previousState.operationType === "delete_object" &&
          mergedOffCanvasForSync.length > 0
        ) {
          // Undoing to delete_object: use same list we set in flushSync so the async load
          // restores the remaining off-canvas image(s) (history has no .image after JSON serialize).
          setOffCanvasWithLoadedImages(mergedOffCanvasForSync);
        } else if (
          isUndoFromMainAddToOffCanvasOnly &&
          mergedOffCanvasForSync.length > 0
        ) {
          // Undoing main-canvas add only: use list that preserves current off-canvas (e.g. crop).
          setOffCanvasWithLoadedImages(mergedOffCanvasForSync);
        } else {
          const restoredOffCanvasImages = prevOffCanvas.filter(
            (img) => !mainCanvasImageIds.has(img.id),
          );
          // Preserve .image from current snapshot for each restored image so they render
          const merged = restoredOffCanvasImages.map((historyImg) => {
            const currentImg = offCanvasAtUndoClick.find(
              (img) => img.id === historyImg.id,
            );
            if (currentImg && (currentImg.image || currentImg.originalImage)) {
              return {
                ...historyImg,
                image: currentImg.image ?? historyImg.image,
                originalImage:
                  currentImg.originalImage ?? historyImg.originalImage,
                compressedImage:
                  currentImg.compressedImage ?? historyImg.compressedImage,
              };
            }
            return historyImg;
          });
          setOffCanvasWithLoadedImages(merged);
        }
        setOffCanvasScribbles(previousState.offCanvasScribbles || []);
        setOffCanvasLines(previousState.offCanvasLines || []);
        setOffCanvasVideos(previousState.offCanvasVideos || []);

        // Restore selectedText state if it exists in history
        if (previousState.selectedText) {
          setSelectedText(previousState.selectedText);
        }

        // Restore selectedTextId if it exists in history
        if (previousState.selectedTextId) {
          setSelectedTextId(previousState.selectedTextId);
        }

        // Restore canvas height if it exists in history
        if (previousState.canvasHeight !== undefined && setCanvasHeight) {
          setCanvasHeight(String(previousState.canvasHeight));
        }

        // Handle images - preserve existing image objects to prevent flicker
        if (previousState.images && previousState.images.length > 0) {
          // When undoing add_image that only added an off-canvas image, keep current main canvas images
          // (with rotation/transforms) so we don't restore stale history (e.g. rotation 0).
          if (isUndoOffCanvasAddOnly && images && images.length > 0) {
            const mainIdsFromPrev = new Set(
              (previousState.images || []).map((img) => img.id),
            );
            setImages(images.filter((img) => mainIdsFromPrev.has(img.id)));
          } else {
            // CRITICAL: Filter out any images that are in offCanvasImages to prevent duplicates
            // This ensures images moved to off-canvas don't appear in main canvas on undo
            const offCanvasImageIds = new Set(
              (previousState.offCanvasImages || []).map((img) => img.id),
            );

            // CRITICAL: If we're undoing from a cut operation and the target state doesn't have cropData,
            // enhance the images with cropData from the cut state

            // CRITICAL: When undoing from a cut operation, we should restore the CROPPED image that was cut,
            // not the original uncropped image. The cut operation removed the cropped portion, so undoing
            // should restore what was actually cut (the cropped image).
            // Only enhance with cropData if we're undoing FROM a cut operation
            const shouldEnhanceWithCropData =
              isCutOperation &&
              finalCutState &&
              previousState.images &&
              previousState.images.length > 0;

            const enhancedPreviousStateImages = (previousState.images || [])
              .filter((img) => !offCanvasImageIds.has(img.id)) // Exclude off-canvas images
              .map((historyImg) => {
                // CRITICAL: When restoring images to main canvas, ensure isOnTopLayer is true
                // This fixes the issue where images dragged to off-canvas and then undone
                // would have isOnTopLayer: false and not render properly
                const restoredImg = {
                  ...historyImg,
                  isOnTopLayer: true, // Always set to true for main canvas images
                };

                // Enhance with cropData if we're undoing FROM a cut operation AND the history image doesn't have cropData
                if (
                  shouldEnhanceWithCropData &&
                  !historyImg.cropData &&
                  cutStateImagesMap.has(historyImg.id)
                ) {
                  const cutStateImg = cutStateImagesMap.get(historyImg.id);
                  if (cutStateImg.cropData) {
                    return {
                      ...restoredImg,
                      cropData: cutStateImg.cropData,
                      imageDataUrl:
                        cutStateImg.imageDataUrl || historyImg.imageDataUrl,
                      originalImageDataUrl:
                        cutStateImg.originalImageDataUrl ||
                        historyImg.originalImageDataUrl,
                      originalCropRotation:
                        cutStateImg.originalCropRotation ||
                        historyImg.originalCropRotation,
                      width: cutStateImg.width || historyImg.width,
                      height: cutStateImg.height || historyImg.height,
                    };
                  }
                }
                // If not enhancing, return the restored image with isOnTopLayer: true
                return restoredImg;
              });

            // Match images by ID and reuse image objects from current state to prevent flicker
            const imagesWithPreservedObjects = enhancedPreviousStateImages.map(
              (historyImg) => {
                // Find matching image in current state by ID
                const currentImg = images?.find(
                  (img) => img.id === historyImg.id,
                );

                // CRITICAL: Check if image data has changed (src, imageDataUrl, or cropData)
                // If image data changed (e.g., after crop), we must reload it, not reuse old object
                const imageDataChanged =
                  currentImg &&
                  (historyImg.src !== currentImg.src ||
                    historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                    JSON.stringify(historyImg.cropData) !==
                    JSON.stringify(currentImg.cropData) ||
                    historyImg.originalImageDataUrl !==
                    currentImg.originalImageDataUrl);

                // If we have a matching image with loaded image objects AND image data hasn't changed, reuse them
                // This preserves images even when their position changes (e.g., after drag)
                // But reloads them if the image data itself changed (e.g., after crop)
                // CRITICAL: Use historyImg as the base to preserve ALL properties from history (rotation, scaleX, scaleY, flipHorizontal, flipVertical, etc.)
                if (
                  currentImg &&
                  currentImg.image &&
                  currentImg.originalImage &&
                  !imageDataChanged
                ) {
                  // CRITICAL: Calculate scaleX/scaleY based on flipHorizontal/flipVertical
                  const isApiImage =
                    historyImg.src &&
                    (historyImg.src.startsWith("http://") ||
                      historyImg.src.startsWith("https://"));
                  const shouldApplyFlipViaScale =
                    isApiImage || !historyImg.hasBakedTransforms;
                  const flipH = historyImg.flipHorizontal || false;
                  const flipV = historyImg.flipVertical || false;
                  const scaleX =
                    shouldApplyFlipViaScale && flipH
                      ? -Math.abs(historyImg.scaleX || 1)
                      : Math.abs(historyImg.scaleX || 1);
                  const scaleY =
                    shouldApplyFlipViaScale && flipV
                      ? -Math.abs(historyImg.scaleY || 1)
                      : Math.abs(historyImg.scaleY || 1);

                  return {
                    ...historyImg, // Use history data (position, size, rotation, scaleX, scaleY, etc.)
                    image: currentImg.image, // Preserve loaded image object
                    originalImage: currentImg.originalImage, // Preserve loaded original image object
                    compressedImage: currentImg.compressedImage, // Preserve compressed image if exists
                    // CRITICAL: Explicitly preserve rotation, scaleX, scaleY, flipHorizontal, flipVertical from history to ensure they're not lost
                    rotation: historyImg.rotation ?? 0,
                    scaleX: scaleX,
                    scaleY: scaleY,
                    flipHorizontal: flipH,
                    flipVertical: flipV,
                    hasBakedTransforms: historyImg.hasBakedTransforms,
                    // CRITICAL: Preserve all crop-related properties from history
                    cropData: historyImg.cropData,
                    originalCropRotation: historyImg.originalCropRotation,
                    // Use imageDataUrl from history (cropped version) as src if it exists
                    src: historyImg.imageDataUrl || historyImg.src,
                    imageDataUrl: historyImg.imageDataUrl,
                    originalImageDataUrl: historyImg.originalImageDataUrl,
                  };
                }

                // Otherwise, return history image as-is (will be loaded async)
                // CRITICAL: Ensure rotation, scaleX, scaleY, flipHorizontal, flipVertical are explicitly set
                const isApiImage =
                  historyImg.src &&
                  (historyImg.src.startsWith("http://") ||
                    historyImg.src.startsWith("https://"));
                const shouldApplyFlipViaScale =
                  isApiImage || !historyImg.hasBakedTransforms;
                const flipH = historyImg.flipHorizontal || false;
                const flipV = historyImg.flipVertical || false;
                const scaleX =
                  shouldApplyFlipViaScale && flipH
                    ? -Math.abs(historyImg.scaleX || 1)
                    : Math.abs(historyImg.scaleX || 1);
                const scaleY =
                  shouldApplyFlipViaScale && flipV
                    ? -Math.abs(historyImg.scaleY || 1)
                    : Math.abs(historyImg.scaleY || 1);

                return {
                  ...historyImg,
                  rotation: historyImg.rotation ?? 0,
                  scaleX: scaleX,
                  scaleY: scaleY,
                  flipHorizontal: flipH,
                  flipVertical: flipV,
                  hasBakedTransforms: historyImg.hasBakedTransforms,
                  // CRITICAL: Preserve all crop-related properties from history
                  cropData: historyImg.cropData,
                  originalCropRotation: historyImg.originalCropRotation,
                  // Use imageDataUrl from history (cropped version) as src if it exists
                  src: historyImg.imageDataUrl || historyImg.src,
                  imageDataUrl: historyImg.imageDataUrl,
                  originalImageDataUrl: historyImg.originalImageDataUrl,
                };
              },
            );

            // Set images immediately with preserved objects to prevent flicker
            setImages(imagesWithPreservedObjects);

            // Only load images that don't have preserved objects (don't reload existing ones)
            // CRITICAL: Also reload if image data changed (e.g., after crop)
            // Use enhanced images (with cropData from cut state if available)
            const imagesToLoad = enhancedPreviousStateImages.filter(
              (historyImg) => {
                const currentImg = images?.find(
                  (img) => img.id === historyImg.id,
                );

                // Check if image data has changed
                const imageDataChanged =
                  currentImg &&
                  (historyImg.src !== currentImg.src ||
                    historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                    JSON.stringify(historyImg.cropData) !==
                    JSON.stringify(currentImg.cropData) ||
                    historyImg.originalImageDataUrl !==
                    currentImg.originalImageDataUrl);

                // Load if we don't have a matching image with loaded objects OR if image data changed
                return !(
                  currentImg &&
                  currentImg.image &&
                  currentImg.originalImage &&
                  !imageDataChanged
                );
              },
            );

            // Load missing images asynchronously in background (non-blocking)
            if (imagesToLoad.length > 0) {
              // CRITICAL: For cropped images, ensure we use imageDataUrl (cropped version) not originalImageDataUrl
              // restoreImagesFromHistory prioritizes originalImageDataUrl, so we need to temporarily remove it
              // for cropped images so it uses imageDataUrl instead
              const imagesForRestore = imagesToLoad.map((img) => {
                // Check if image has cropData (cropped image)
                // cropData can be an object with left, top, width, height properties
                const hasCropData =
                  img.cropData !== null &&
                  img.cropData !== undefined &&
                  typeof img.cropData === "object" &&
                  (Object.keys(img.cropData).length > 0 ||
                    img.cropData.left !== undefined ||
                    img.cropData.top !== undefined ||
                    img.cropData.width !== undefined ||
                    img.cropData.height !== undefined);
                const hasImageDataUrl =
                  img.imageDataUrl && img.imageDataUrl.trim() !== "";

                // Debug logging to see what's in the history entry

                // If image has cropData, use imageDataUrl (cropped version) as the source
                if (hasCropData && hasImageDataUrl) {
                  // Temporarily remove originalImageDataUrl so restoreImagesFromHistory uses imageDataUrl
                  const { originalImageDataUrl, ...imgWithoutOriginal } = img;
                  return {
                    ...imgWithoutOriginal,
                    src: img.imageDataUrl, // Use cropped image data URL
                    // CRITICAL: Don't include originalImageDataUrl so restoreImagesFromHistory uses imageDataUrl
                    // But preserve it in a different property so we can restore it later
                    _originalImageDataUrl: originalImageDataUrl, // Store temporarily
                  };
                }
                // Otherwise, use originalImageDataUrl if available, or src
                if (img.originalImageDataUrl) {
                  return {
                    ...img,
                    src: img.originalImageDataUrl,
                  };
                }
                return img;
              });

              // Restore images from history - just use the images as-is
              Promise.resolve(imagesForRestore)
                .then((imagesToActuallyLoad) =>
                  Promise.all(
                    (imagesToActuallyLoad || []).map((img) =>
                      createImageLoadPromise({ imgData: img, info: img }),
                    ),
                  ),
                )
                .then((restoredImages) => {
                  // Merge restored images with preserved ones
                  if (restoredImages && restoredImages.length > 0) {
                    const finalImages = enhancedPreviousStateImages.map(
                      (historyImg) => {
                        const currentImg = images?.find(
                          (img) => img.id === historyImg.id,
                        );

                        // CRITICAL: Check if image data has changed
                        const imageDataChanged =
                          currentImg &&
                          (historyImg.src !== currentImg.src ||
                            historyImg.imageDataUrl !==
                            currentImg.imageDataUrl ||
                            JSON.stringify(historyImg.cropData) !==
                            JSON.stringify(currentImg.cropData) ||
                            historyImg.originalImageDataUrl !==
                            currentImg.originalImageDataUrl);

                        // If we already have a loaded image object AND image data hasn't changed, keep it
                        // CRITICAL: Preserve rotation, scaleX, scaleY, flipHorizontal, flipVertical from history
                        if (
                          currentImg &&
                          currentImg.image &&
                          currentImg.originalImage &&
                          !imageDataChanged
                        ) {
                          // CRITICAL: Calculate scaleX/scaleY based on flipHorizontal/flipVertical
                          const isApiImage =
                            historyImg.src &&
                            (historyImg.src.startsWith("http://") ||
                              historyImg.src.startsWith("https://"));
                          const shouldApplyFlipViaScale =
                            isApiImage || !historyImg.hasBakedTransforms;
                          const flipH = historyImg.flipHorizontal || false;
                          const flipV = historyImg.flipVertical || false;
                          const scaleX =
                            shouldApplyFlipViaScale && flipH
                              ? -Math.abs(historyImg.scaleX || 1)
                              : Math.abs(historyImg.scaleX || 1);
                          const scaleY =
                            shouldApplyFlipViaScale && flipV
                              ? -Math.abs(historyImg.scaleY || 1)
                              : Math.abs(historyImg.scaleY || 1);

                          return {
                            ...historyImg,
                            image: currentImg.image,
                            originalImage: currentImg.originalImage,
                            compressedImage: currentImg.compressedImage,
                            // CRITICAL: Explicitly preserve rotation, scaleX, scaleY, flipHorizontal, flipVertical from history
                            rotation: historyImg.rotation ?? 0,
                            scaleX: scaleX,
                            scaleY: scaleY,
                            flipHorizontal: flipH,
                            flipVertical: flipV,
                            hasBakedTransforms: historyImg.hasBakedTransforms,
                            // CRITICAL: Preserve all crop-related properties from history
                            cropData: historyImg.cropData,
                            originalCropRotation:
                              historyImg.originalCropRotation,
                            // Use imageDataUrl from history (cropped version) as src if it exists
                            src: historyImg.imageDataUrl || historyImg.src,
                            imageDataUrl: historyImg.imageDataUrl,
                            originalImageDataUrl:
                              historyImg.originalImageDataUrl,
                          };
                        }
                        // Otherwise, find the restored image (which has the correct image data loaded)
                        const restoredImg = restoredImages.find(
                          (img) => img.id === historyImg.id,
                        );
                        if (restoredImg) {
                          // CRITICAL: Calculate scaleX/scaleY based on flipHorizontal/flipVertical
                          const isApiImage =
                            historyImg.src &&
                            (historyImg.src.startsWith("http://") ||
                              historyImg.src.startsWith("https://"));
                          const shouldApplyFlipViaScale =
                            isApiImage || !historyImg.hasBakedTransforms;
                          const flipH = historyImg.flipHorizontal || false;
                          const flipV = historyImg.flipVertical || false;
                          const scaleX =
                            shouldApplyFlipViaScale && flipH
                              ? -Math.abs(historyImg.scaleX || 1)
                              : Math.abs(historyImg.scaleX || 1);
                          const scaleY =
                            shouldApplyFlipViaScale && flipV
                              ? -Math.abs(historyImg.scaleY || 1)
                              : Math.abs(historyImg.scaleY || 1);

                          // CRITICAL: Restore originalImageDataUrl if it was temporarily stored
                          const restoredOriginalImageDataUrl =
                            historyImg._originalImageDataUrl ||
                            historyImg.originalImageDataUrl;

                          // Ensure rotation, scaleX, scaleY, flipHorizontal, flipVertical are preserved
                          return {
                            ...restoredImg,
                            ...historyImg, // Use history data as base to preserve all properties
                            image: restoredImg.image,
                            originalImage:
                              restoredImg.originalImage || restoredImg.image,
                            compressedImage: restoredImg.compressedImage,
                            rotation: historyImg.rotation ?? 0,
                            scaleX: scaleX,
                            scaleY: scaleY,
                            flipHorizontal: flipH,
                            flipVertical: flipV,
                            hasBakedTransforms: historyImg.hasBakedTransforms,
                            // CRITICAL: Preserve all crop-related properties from history
                            cropData: historyImg.cropData,
                            originalCropRotation:
                              historyImg.originalCropRotation,
                            // Use imageDataUrl from history (cropped version) as src if it exists
                            src: historyImg.imageDataUrl || historyImg.src,
                            imageDataUrl: historyImg.imageDataUrl,
                            originalImageDataUrl: restoredOriginalImageDataUrl,
                            // Remove temporary property if it exists
                            _originalImageDataUrl: undefined,
                          };
                        }
                        // Fallback: return historyImg with explicit rotation, scaleX, scaleY, flipHorizontal, flipVertical
                        const isApiImage =
                          historyImg.src &&
                          (historyImg.src.startsWith("http://") ||
                            historyImg.src.startsWith("https://"));
                        const shouldApplyFlipViaScale =
                          isApiImage || !historyImg.hasBakedTransforms;
                        const flipH = historyImg.flipHorizontal || false;
                        const flipV = historyImg.flipVertical || false;
                        const scaleX =
                          shouldApplyFlipViaScale && flipH
                            ? -Math.abs(historyImg.scaleX || 1)
                            : Math.abs(historyImg.scaleX || 1);
                        const scaleY =
                          shouldApplyFlipViaScale && flipV
                            ? -Math.abs(historyImg.scaleY || 1)
                            : Math.abs(historyImg.scaleY || 1);

                        return {
                          ...historyImg,
                          rotation: historyImg.rotation ?? 0,
                          scaleX: scaleX,
                          scaleY: scaleY,
                          flipHorizontal: flipH,
                          flipVertical: flipV,
                          hasBakedTransforms: historyImg.hasBakedTransforms,
                          // CRITICAL: Preserve all crop-related properties from history
                          cropData: historyImg.cropData,
                          originalCropRotation: historyImg.originalCropRotation,
                          // Use imageDataUrl from history (cropped version) as src if it exists
                          src: historyImg.imageDataUrl || historyImg.src,
                          imageDataUrl: historyImg.imageDataUrl,
                          originalImageDataUrl: historyImg.originalImageDataUrl,
                        };
                      },
                    );
                    setImages(finalImages);
                  }
                })
                .catch(() => {
                  // Silently fail - images already set with preserved objects
                });
            }
          }
        } else {
          // CRITICAL: Don't clear images if previousState.images is undefined/null
          // Only clear if it's explicitly an empty array (meaning the state intentionally had no images)
          // If previousState.images is undefined, it might mean the state wasn't properly saved
          // In that case, preserve current images to prevent accidental deletion
          if (
            previousState.images === undefined ||
            previousState.images === null
          ) {
            // Don't modify images - preserve current state
            console.warn(
              "⚠️ [UNDO] previousState.images is undefined/null, preserving current images",
            );
          } else if (previousState.images.length === 0) {
            // Empty array - this means the previous state had no images
            // CRITICAL: Only clear images if we're undoing to the initial state (operationType === "initial")
            // AND the current state (before undo) also has no images
            // If the current state has images but we're undoing to initial, we should preserve
            // the original uncropped image from the current state (which should be from add_preset_image)
            const isInitialState = previousState.operationType === "initial";
            const currentStateHasImages = images && images.length > 0;

            if (isInitialState && !currentStateHasImages) {
              // Clear all images when undoing to the initial state AND current state has no images
              setImages([]);
            } else if (isInitialState && currentStateHasImages) {
              // Undoing to initial state but current state has images
              // This means we're undoing from add_preset_image to initial
              // CRITICAL: We need to distinguish between two scenarios:
              // 1. After cut operation: Current images have cropData (enhanced), so show original image
              // 2. Normal crop undo: Current images don't have cropData, so clear image (undo add_preset_image)
              const stateBeingUndoneFrom = currentState;

              // Check if the current images have cropData (indicating they were enhanced from a cut operation)
              const currentImagesHaveCropData =
                images &&
                images.some((img) => {
                  const hasCropData =
                    img.cropData !== null &&
                    img.cropData !== undefined &&
                    typeof img.cropData === "object" &&
                    (Object.keys(img.cropData).length > 0 ||
                      img.cropData.left !== undefined ||
                      img.cropData.top !== undefined ||
                      img.cropData.width !== undefined ||
                      img.cropData.height !== undefined);
                  return hasCropData;
                });

              if (
                stateBeingUndoneFrom?.operationType === "add_preset_image" &&
                stateBeingUndoneFrom.images &&
                stateBeingUndoneFrom.images.length > 0 &&
                currentImagesHaveCropData
              ) {
                // Scenario 1: After cut operation - restore original image from add_preset_image state

                // Restore original uncropped images from add_preset_image state
                // Remove any cropData that might have been added during enhancement
                const imagesToRestore = stateBeingUndoneFrom.images.map(
                  (img) => {
                    const { imageDataUrl, cropData, ...imgBase } = img;
                    return {
                      ...imgBase,
                      cropData: null, // Remove cropData - add_preset_image should have original
                      imageDataUrl: null, // Remove cropped imageDataUrl
                      src: img.originalImageDataUrl || img.src, // Use original image
                      originalImageDataUrl: img.originalImageDataUrl || img.src,
                      image: null, // Will be reloaded
                      originalImage: null,
                      compressedImage: null,
                    };
                  },
                );

                // Use images from history - just use them as-is
                const generateMarkerId = () =>
                  `marker-${Date.now()}-${uuidv4()}`;

                Promise.resolve(imagesToRestore)
                  .then((restoredImages) => {
                    if (restoredImages && restoredImages.length > 0) {
                      setImages(restoredImages);
                    } else {
                      console.warn("⚠️ [UNDO] No images restored, clearing");
                      setImages([]);
                    }
                  })
                  .catch((error) => {
                    console.error(
                      "❌ [UNDO] Error restoring original images:",
                      error,
                    );
                    setImages([]);
                  });
              } else {
                // Scenario 2: Normal crop undo - clear images (undo add_preset_image operation)
                setImages([]);
              }
            } else {
              // Not initial state but previousState.images is []
              // For add_image/add_image_clone: that entry was off-canvas only, so clear main canvas.
              // For element_edit_complete / image_drag_end: can be after drag to off-canvas (main has 0 images in snapshot).
              // For delete_object: we're restoring to state after a delete; main may be empty and off-canvas is restored from previousState.offCanvasImages.
              const shouldClearMainImages =
                previousState.operationType === "add_image" ||
                previousState.operationType === "add_image_clone" ||
                previousState.operationType === "element_edit_complete" ||
                previousState.operationType === "image_drag_end" ||
                previousState.operationType === "delete_object";
              if (shouldClearMainImages) {
                setImages([]);
              } else {
                // Other ops with no images - might be serialization issue, preserve to be safe
                console.warn(
                  "⚠️ [UNDO] previousState has no images but is not initial state, preserving current images",
                  {
                    operationType: previousState.operationType,
                    currentImagesCount: images?.length || 0,
                    currentImageIds: images?.map((img) => img.id) || [],
                  },
                );
              }
            }
          } else {
            // Should not reach here, but just in case
            setImages([]);
          }
        }
      };

      // Execute the restoration synchronously for instant response
      restoreCanvasState();

      // DEBUG: Log after undo restoration
      // Get the actual restored image from state (after setImages is called)
      // We'll log what was in previousState and what should be restored
      const restoredImageFromHistory = previousState?.images?.[0]; // Get first image directly

      // Note: isUndoingRedoing will be reset immediately for instant response
    } catch (error) {
    } finally {
      // Defer reset so any setTimeout(0) from updateImageProperties (triggered by setOffCanvasImages
      // during restore) runs while isUndoingRedoing is still true and skips saveToHistory. Otherwise
      // we get an extra history entry and the undo button stays enabled after undoing off-canvas image.
      setTimeout(() => {
        isUndoingRedoing.current = false;
        if (lastUndoRedoCompletionTime) {
          lastUndoRedoCompletionTime.current = Date.now();
        }
      }, 10);
    }
  };
}

/**
 * Creates a redo function that restores the next canvas state
 * @param {Array} redoStack - Current redo stack array
 * @param {Array} history - Current history array (for comparison)
 * @param {Function} setHistory - Function to set history state
 * @param {Function} setRedoStack - Function to set redo stack state
 * @param {React.RefObject} isUndoingRedoing - Ref to track if undo/redo is in progress
 * @param {React.RefObject} lastUndoRedoCompletionTime - Ref to track when undo/redo last completed
 * @param {string} fillColor - Current fill color
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setSelectedTextId - Setter for selected text ID
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedArrow - Setter for selected arrow
 * @param {Function} setSelectedLine - Setter for selected line
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} setLines - Setter for lines array
 * @param {Function} setRectangles - Setter for rectangles array
 * @param {Function} setCircles - Setter for circles array
 * @param {Function} setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} setVideos - Setter for videos array
 * @param {Function} setScribbles - Setter for scribbles array
 * @param {Function} setOffCanvasTexts - Setter for off-canvas texts array
 * @param {Function} setOffCanvasRectangles - Setter for off-canvas rectangles array
 * @param {Function} setOffCanvasCircles - Setter for off-canvas circles array
 * @param {Function} setOffCanvasArrows - Setter for off-canvas arrows array
 * @param {Function} setOffCanvasDoubleSidedArrows - Setter for off-canvas double-sided arrows array
 * @param {Function} setOffCanvasImages - Setter for off-canvas images array
 * @param {Function} setOffCanvasScribbles - Setter for off-canvas scribbles array
 * @param {Function} setOffCanvasLines - Setter for off-canvas lines array
 * @param {Function} setOffCanvasVideos - Setter for off-canvas videos array
 * @param {Function} setImages - Setter for images array
 * @returns {Function} Redo function
 */

// Safety flags for legacy circle/off-canvas redo heuristics.
// These are intentionally hard-coded to false so they never change behavior,
// but keep any remaining references defined to satisfy ESLint in older bundles.
const isActuallyOffCanvasCircleOperation = false;
const isAddCircleOffCanvasStoredInMainRedo = false;

export function createRedo({
  redoStack,
  history, // Current history for comparison
  setHistory,
  setRedoStack,
  isUndoingRedoing,
  lastUndoRedoCompletionTime,
  fillColor,
  setSelectedObject,
  setSelectedText,
  setSelectedTextId,
  setSelectedImage,
  setSelectedArrow,
  setSelectedLine,
  setSelectedObjects,
  setArrows,
  setLines,
  setRectangles,
  setCircles,
  setDoubleSidedArrows,
  setTexts,
  setVideos,
  setScribbles,
  setOffCanvasTexts,
  setOffCanvasRectangles,
  setOffCanvasCircles,
  setOffCanvasArrows,
  setOffCanvasDoubleSidedArrows,
  setOffCanvasImages,
  setOffCanvasScribbles,
  setOffCanvasLines,
  setOffCanvasVideos,
  setImages,
  images, // Current images state - needed to preserve image objects
  setCanvasHeight,
}) {
  return function redo() {
    if (redoStack.length === 0) {
      return;
    }

    // Prevent recursive calls
    if (isUndoingRedoing.current) {
      return;
    }

    isUndoingRedoing.current = true;
    if (lastUndoRedoCompletionTime) {
      lastUndoRedoCompletionTime.current = Date.now();
    }

    try {
      // Get current state from history for comparison
      const currentHistoryState =
        history.length > 0 ? history[history.length - 1] : null;

      // Find the first state in redo stack that's different from current history state
      // Skip identical states (e.g., from opening/closing text editor without changes)
      // CRITICAL: Process redo stack in order (first to last) to maintain correct sequence
      let targetStateIndex = 0; // Start with first state in redo stack
      let skippedCount = 0;
      const MAX_SKIPS = 50; // Safety limit to prevent infinite loops

      // Compare states in redo stack with current history state
      // Start from the beginning and work forwards
      while (targetStateIndex < redoStack.length && skippedCount < MAX_SKIPS) {
        const candidateState = redoStack[targetStateIndex];

        // If we have a current history state, compare with it
        // Otherwise, use the first state
        if (currentHistoryState) {
          // If states are different, use this one
          if (!areStatesEqual(candidateState, currentHistoryState)) {
            break;
          } else {
          }
        } else {
          // No history state, use the first state
          break;
        }

        // States are the same, skip to next one
        skippedCount++;
        targetStateIndex++;
      }

      // If we've gone through all states or hit the limit, use the last available state
      if (targetStateIndex >= redoStack.length) {
        targetStateIndex = redoStack.length - 1;
      }

      const stateFromRedoStack = redoStack[targetStateIndex];
      let statesToRestore = [stateFromRedoStack];

      // When undo grouped add_text + element_edit_complete into a single step, the redo stack
      // will contain both states (e.g. ["add_text", "element_edit_complete"]). In that case,
      // a single redo should restore BOTH states and clear them from the redo stack so the
      // toolbar disables redo immediately after the text reappears.
      const nextRedoState = redoStack[targetStateIndex + 1];
      const isGroupedTextRedo =
        stateFromRedoStack &&
        (stateFromRedoStack.operationType === "add_text" ||
          stateFromRedoStack.operationType === "add_text_clone") &&
        nextRedoState &&
        nextRedoState.operationType === "element_edit_complete" &&
        currentHistoryState &&
        currentHistoryState.operationType === "element_edit_complete";

      // Indices we will remove from redo stack after redo completes
      const indicesToRemoveFromRedo = [targetStateIndex];

      if (isGroupedTextRedo) {
        statesToRestore = [stateFromRedoStack, nextRedoState];
        indicesToRemoveFromRedo.push(targetStateIndex + 1);
      }

      // When redoing add_rectangle/add_circle/add_text that was drawn on off-canvas, snapshot may have
      // stored items in main array (rectangles/circles/texts) with empty offCanvas*. Push a corrected
      // state to history so the top entry has items only in offCanvas*, and nothing syncs them to main.
      // CRITICAL FIX: Only treat as "wrong storage" if we can detect element was actually added to off-canvas
      // by checking if element IDs exist in off-canvas arrays in the state being redone
      const redoStateOffCanvasCircleIds = new Set(
        (stateFromRedoStack?.offCanvasCircles || []).map((c) => c.id),
      );
      const redoStateCircleIds = new Set(
        (stateFromRedoStack?.circles || []).map((c) => c.id),
      );

      // Check if any circle from redo state exists in off-canvas (indicating it was added to off-canvas)
      const redoStateHasCirclesInOffCanvasCheck = [...redoStateCircleIds].some(
        (id) => redoStateOffCanvasCircleIds.has(id),
      );

      // Disable rectangle "wrong storage" auto-fix for redo.
      // Off-canvas rectangles should already be stored in offCanvasRectangles
      // by historyUtils; moving main rectangles into off-canvas here causes
      // true main-canvas rectangles to reappear on off-canvas on repeated redo.
      const isAddRectWrongStorage = false;
      const isAddCircleWrongStorage =
        (stateFromRedoStack?.operationType === "add_circle" ||
          stateFromRedoStack?.operationType === "add_circle_clone") &&
        (stateFromRedoStack.offCanvasCircles || []).length === 0 &&
        (stateFromRedoStack.circles || []).length > 0 &&
        !redoStateHasCirclesInOffCanvasCheck; // Only treat as wrong if circles are NOT in off-canvas
      // IMPORTANT: Disable "wrong storage" auto-fix for add_text.
      // Earlier logic tried to infer off-canvas adds by moving texts
      // from `texts` to `offCanvasTexts` when offCanvasTexts was empty,
      // but this incorrectly reclassified valid main-canvas texts as
      // off-canvas, which then broke undo/redo for API/main texts.
      const isAddTextWrongStorage = false;

      const stateToPushToHistory = isAddRectWrongStorage
        ? {
          ...stateFromRedoStack,
          rectangles: [],
          offCanvasRectangles: stateFromRedoStack.rectangles || [],
        }
        : isAddCircleWrongStorage
          ? {
            ...stateFromRedoStack,
            circles: [],
            offCanvasCircles: stateFromRedoStack.circles || [],
          }
          : isAddTextWrongStorage
            ? {
              ...stateFromRedoStack,
              texts: [],
              offCanvasTexts: stateFromRedoStack.texts || [],
            }
            : stateFromRedoStack;

      // DEBUG: Log redo operation

      // Add states back to history and remove from redo stack in the same flushSync
      // so the Toolbar sees the updated redo stack (e.g. empty) immediately and disables
      // the redo button before we run the rest of the restoration (setTexts, setOffCanvasTexts, etc.).
      flushSync(() => {
        setHistory((prev) => [...prev, ...statesToRestore]);
        setRedoStack((prev) => {
          const newStack = prev.filter(
            (_, index) => !indicesToRemoveFromRedo.includes(index),
          );
          return newStack;
        });
      });

      // Use the last state in the restored batch as the state to restore visually
      const finalStateToRedo = statesToRestore[statesToRestore.length - 1];

      // CRITICAL: If we're redoing a delete_object or delete_multiple_objects, we need to actually delete the element(s)
      // The state contains the state BEFORE deletion; redoing should delete, not restore
      const isRedoingDelete =
        finalStateToRedo?.operationType === "delete_object";
      const isRedoingDeleteMultiple =
        finalStateToRedo?.operationType === "delete_multiple_objects";

      // CRITICAL: If we're redoing a cut_image, we need to actually remove the image
      // The cut_image state contains the state BEFORE cutting (with the image),
      // but redoing it should remove the image, not restore the state before cutting
      const isRedoingCut = finalStateToRedo?.operationType === "cut_image";

      if (isRedoingCut) {
        // CRITICAL: The cut_image state contains the image that was cut
        // We need to remove that image from the canvas, not restore it
        const cutImageId = finalStateToRedo.images?.[0]?.id;

        // Remove the image that was cut
        if (cutImageId) {
          setImages((prev) => prev.filter((img) => img.id !== cutImageId));
        } else {
          console.error("✂️ [REDO] Missing image ID in cut_image state", {
            finalStateToRedo,
          });
          // Fallback: clear all images if we can't find the specific one
          setImages([]);
        }

        // Clear selections
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Mark undo/redo as complete
        isUndoingRedoing.current = false;
        if (lastUndoRedoCompletionTime) {
          lastUndoRedoCompletionTime.current = Date.now();
        }
        return; // Exit early - don't restore the state
      }

      if (isRedoingDeleteMultiple) {
        // Redo multi-delete: remove all objects whose ids are in deletedObjectId (comma-separated)
        const idStr = finalStateToRedo.deletedObjectId;
        const typeStr = finalStateToRedo.deletedObjectType;
        if (idStr && typeStr) {
          const deletedIds = idStr
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          const idSet = new Set(deletedIds);
          if (setRectangles) {
            setRectangles((prev) => prev.filter((r) => !idSet.has(r.id)));
          }
          if (setCircles) {
            setCircles((prev) => prev.filter((c) => !idSet.has(c.id)));
          }
          if (setArrows) {
            setArrows((prev) => prev.filter((a) => !idSet.has(a.id)));
          }
          if (setDoubleSidedArrows) {
            setDoubleSidedArrows((prev) =>
              prev.filter((a) => !idSet.has(a.id)),
            );
          }
          if (setLines) {
            setLines((prev) => prev.filter((l) => !idSet.has(l.id)));
          }
          if (setTexts) {
            setTexts((prev) => prev.filter((t) => !idSet.has(t.id)));
          }
          if (setImages) {
            setImages((prev) => prev.filter((img) => !idSet.has(img.id)));
          }
          if (setOffCanvasImages) {
            setOffCanvasImages((prev) =>
              prev.filter((img) => !idSet.has(img.id)),
            );
          }
          if (setOffCanvasRectangles) {
            setOffCanvasRectangles((prev) =>
              prev.filter((r) => !idSet.has(r.id)),
            );
          }
          if (setOffCanvasCircles) {
            setOffCanvasCircles((prev) => prev.filter((c) => !idSet.has(c.id)));
          }
          if (setOffCanvasArrows) {
            setOffCanvasArrows((prev) => prev.filter((a) => !idSet.has(a.id)));
          }
          if (setOffCanvasDoubleSidedArrows) {
            setOffCanvasDoubleSidedArrows((prev) =>
              prev.filter((a) => !idSet.has(a.id)),
            );
          }
          if (setOffCanvasLines) {
            setOffCanvasLines((prev) => prev.filter((l) => !idSet.has(l.id)));
          }
          if (setOffCanvasTexts) {
            setOffCanvasTexts((prev) => prev.filter((t) => !idSet.has(t.id)));
          }
        }
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);
        isUndoingRedoing.current = false;
        if (lastUndoRedoCompletionTime) {
          lastUndoRedoCompletionTime.current = Date.now();
        }
        return;
      }

      if (isRedoingDelete) {
        // CRITICAL: Use deletedObjectId and deletedObjectType to delete only the specific element
        // The delete_object state contains ALL elements that existed before deletion,
        // but we only want to delete the one that was actually deleted
        const deletedObjectId = finalStateToRedo.deletedObjectId;
        const deletedObjectType = finalStateToRedo.deletedObjectType;

        // Delete only the specific element that was deleted, based on its type and ID
        if (deletedObjectId && deletedObjectType) {
          if (deletedObjectType === "Rect") {
            setRectangles((prev) =>
              prev.filter((r) => r.id !== deletedObjectId),
            );
          } else if (deletedObjectType === "Circle") {
            setCircles((prev) => prev.filter((c) => c.id !== deletedObjectId));
          } else if (deletedObjectType === "Arrow") {
            setArrows((prev) => prev.filter((a) => a.id !== deletedObjectId));
          } else if (deletedObjectType === "DoubleSidedArrow") {
            setDoubleSidedArrows((prev) =>
              prev.filter((a) => a.id !== deletedObjectId),
            );
          } else if (deletedObjectType === "Line") {
            setLines((prev) => prev.filter((l) => l.id !== deletedObjectId));
          } else if (deletedObjectType === "Text") {
            setTexts((prev) => prev.filter((t) => t.id !== deletedObjectId));
          } else if (deletedObjectType === "Image") {
            setImages((prev) =>
              prev.filter((img) => img.id !== deletedObjectId),
            );
            // CRITICAL: Also remove from off-canvas when redoing delete of an off-canvas image
            if (setOffCanvasImages) {
              setOffCanvasImages((prev) =>
                prev.filter((img) => img.id !== deletedObjectId),
              );
            }
          } else {
            console.warn("🗑️ [REDO] Unknown deleted object type", {
              deletedObjectType,
              deletedObjectId,
            });
          }
        } else {
          console.error(
            "🗑️ [REDO] Missing deletedObjectId or deletedObjectType in delete_object state",
            {
              deletedObjectId,
              deletedObjectType,
              finalStateToRedo,
            },
          );
        }

        // Clear selections
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Mark undo/redo as complete
        isUndoingRedoing.current = false;
        if (lastUndoRedoCompletionTime) {
          lastUndoRedoCompletionTime.current = Date.now();
        }

        return; // Exit early, don't restore the state
      }

      // DEBUG: Log what's in finalStateToRedo to see if cropData is there
      if (finalStateToRedo.images && finalStateToRedo.images.length > 0) {
        finalStateToRedo.images.forEach((img, index) => { });
      }

      // DEBUG: Log operation type to understand what we're redoing
      // Restore all canvas elements atomically - optimized for speed
      const restoreCanvasState = () => {
        // Helper function to normalize strokeColor (convert number to hex string if needed)
        const normalizeStrokeColor = (color) => {
          if (!color) return "#000000";
          if (typeof color === "string" && color.startsWith("#")) return color;
          if (typeof color === "string") return color; // Already a string
          if (typeof color === "number") {
            return `#${color.toString(16).padStart(6, "0")}`;
          }
          return "#000000";
        };

        // Batch all state updates together for instant restoration
        const restoredArrows = (finalStateToRedo.arrows || []).map((arrow) => {
          // Normalize strokeColor to ensure it's always a string
          const normalizedStroke = normalizeStrokeColor(
            arrow.stroke || arrow.strokeColor,
          );
          return {
            ...arrow,
            stroke: normalizedStroke,
            strokeColor: normalizedStroke,
            angle: arrow.angle ?? 90, // Ensure angle is always present
            isRestored: true,
            isFromHistory: true,
          };
        });

        // Clear all selections first
        setSelectedObject(null);
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedImage(null);
        setSelectedArrow(null);
        setSelectedLine(null);
        setSelectedObjects([]);

        // Restore all elements synchronously - batched together
        // When redoing add_arrow that had 0 main arrows (off-canvas add), preserve current main arrows.
        const isOffCanvasAddArrowRedo =
          (finalStateToRedo.operationType === "add_arrow" ||
            finalStateToRedo.operationType === "add_arrow_clone") &&
          finalStateToRedo.arrows?.length === 0;
        if (isOffCanvasAddArrowRedo) {
          setArrows((prev) => prev);
        } else {
          setArrows(restoredArrows);
        }

        // Ensure all lines have startDot and endDot properties when restored
        const restoredLines = (finalStateToRedo.lines || []).map((line) => {
          // Ensure line has the required dot properties
          const lineWithDots = {
            ...line,
            startDot: line.startDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
            endDot: line.endDot || {
              radius: 4,
              fill: line.strokeColor || line.stroke || fillColor || "black",
              stroke: line.strokeColor || line.stroke || fillColor || "black",
              strokeWidth: 1,
            },
          };
          return lineWithDots;
        });

        // When redoing add_line/add_line_clone that had 0 main lines (off-canvas add), preserve current main lines.
        const isOffCanvasAddLineRedo =
          (finalStateToRedo.operationType === "add_line" ||
            finalStateToRedo.operationType === "add_line_clone") &&
          finalStateToRedo.lines?.length === 0;
        if (isOffCanvasAddLineRedo) {
          setLines((prev) => prev);
        } else {
          setLines(restoredLines);
        }

        // CRITICAL FIX: When restoring rectangles from history, we need to check which canvas they belong to
        // based on the operation type and the state being restored, not just filter by offCanvasRectangles
        // The issue: When a rectangle is added to main canvas, then undone, the undo might save it in offCanvasRectangles
        // incorrectly, causing redo to filter it out and place it in the wrong canvas

        // For most operations, rectangles in finalStateToRedo.rectangles belong to main canvas
        // Only filter out if they're ALSO in offCanvasRectangles AND the operation suggests they should be off-canvas.
        // Special case: for add_rectangle/add_rectangle_clone we NEVER want the same rect id in BOTH main and off-canvas;
        // if that happens, treat off-canvas as the source of truth and drop it from main.
        const offCanvasRectIds = new Set(
          (finalStateToRedo.offCanvasRectangles || []).map((r) => r.id),
        );

        // CRITICAL: Don't filter rectangles from main canvas array unless they're explicitly meant to be off-canvas
        // Check if this is a drag_transfer operation or if the rectangle was explicitly moved to off-canvas
        // ALSO check for element_edit_complete that might have overwritten a drag_transfer
        const isDragTransfer =
          finalStateToRedo.operationType === "drag_transfer";
        const isElementEditAfterTransfer =
          finalStateToRedo.operationType === "element_edit_complete" &&
          finalStateToRedo.images?.length === 0 &&
          finalStateToRedo.offCanvasImages?.length > 0;
        const shouldFilterByOffCanvas =
          isDragTransfer || isElementEditAfterTransfer;

        let redoRectangles = (finalStateToRedo.rectangles || [])
          .filter((rect) => {
            const isAddRectangleOp =
              finalStateToRedo.operationType === "add_rectangle" ||
              finalStateToRedo.operationType === "add_rectangle_clone";

            // Debug logging for add_rectangle operations
            if (isAddRectangleOp) {
            }

            // CRITICAL FIX: For add_rectangle operations, don't filter out rectangles based on offCanvasRectIds
            // This was causing main canvas rectangles to be filtered out due to corrupted history
            if (isAddRectangleOp) {
              return true; // Keep all rectangles for add_rectangle operations
            }
            // For other operations (like drag_transfer), still apply the original filtering logic
            if (shouldFilterByOffCanvas && offCanvasRectIds.has(rect.id)) {
              return false; // Filter out - it belongs to off-canvas
            }
            return true; // Keep it in main canvas
          })
          .map((rect) => ({
            ...rect, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: rect.fillOpacity ?? 1,
            strokeOpacity: rect.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: rect.text ?? "",
            textColor: rect.textColor,
            align: rect.align ?? "center",
            verticalAlign: rect.verticalAlign ?? "middle",
            fontSize: rect.fontSize,
            fontStyle: rect.fontStyle,
            textDecoration: rect.textDecoration,
          }));

        // NOTE: For add_rectangle operations we deliberately *do not* merge offCanvasRectangles
        // into the main rectangles array here. In correct snapshots, offCanvasRectangles are
        // genuine off-canvas shapes and must not be injected into the main canvas.

        // Detect off-canvas-only add_rectangle operations:
        // - main rectangles array is empty
        // - offCanvasRectangles has one or more items
        const isAddRectangleOperation =
          finalStateToRedo.operationType === "add_rectangle" ||
          finalStateToRedo.operationType === "add_rectangle_clone";
        const mainRectCount =
          (finalStateToRedo.rectangles || []).length;
        const offCanvasRectCount =
          (finalStateToRedo.offCanvasRectangles || []).length;

        const isOffCanvasAddRectangleRedo =
          isAddRectangleOperation &&
          mainRectCount === 0 &&
          offCanvasRectCount > 0;

        // CRITICAL FIX: Properly handle off-canvas-only add_rectangle operations
        // If this was an off-canvas add_rectangle, we need to restore the rectangle to off-canvas

        if (isOffCanvasAddRectangleRedo) {
          // This was an off-canvas add_rectangle operation
          // Preserve current main canvas rectangles and restore off-canvas rectangles
          // Don't modify main canvas rectangles - they should stay as they are
          // The off-canvas rectangles will be restored below
        } else if (
          finalStateToRedo.operationType === "add_rectangle" ||
          finalStateToRedo.operationType === "add_rectangle_clone"
        ) {
          // Merge redo rectangles into existing main rectangles so API rectangles are preserved
          // and the newly added rectangle is reliably restored.
          setRectangles((prev) => {
            const baseRects = Array.isArray(prev) ? prev : [];
            const existingIds = new Set(baseRects.map((r) => r.id));
            const mergedRects = [
              ...baseRects,
              ...redoRectangles.filter((r) => !existingIds.has(r.id)),
            ];

            return mergedRects;
          });
        } else {
          setRectangles(redoRectangles);
        }

        // CRITICAL FIX: When restoring circles from history, we need to check which canvas they belong to
        // based on the operation type and the state being restored, not just filter by offCanvasCircles
        // The issue: When a circle is added to main canvas, then undone, the undo saves it in offCanvasCircles
        // incorrectly, causing redo to filter it out and place it in the wrong canvas

        // For most operations, circles in finalStateToRedo.circles belong to main canvas
        // Only filter out if they're ALSO in offCanvasCircles AND the operation suggests they should be off-canvas
        const offCanvasCircleIds = new Set(
          (finalStateToRedo.offCanvasCircles || []).map((c) => c.id),
        );

        // CRITICAL: Use of same isDragTransfer check as rectangles (already declared above)
        let redoCircles = (finalStateToRedo.circles || [])
          .filter((circle) => {
            const isAddCircleOp =
              finalStateToRedo.operationType === "add_circle" ||
              finalStateToRedo.operationType === "add_circle_clone";
            // CRITICAL FIX: For add_circle operations, don't filter out circles based on offCanvasCircleIds
            // This was causing main canvas circles to be filtered out due to corrupted history
            if (isAddCircleOp) {
              return true; // Keep all circles for add_circle operations
            }
            // For other operations (like drag_transfer), still apply the original filtering logic
            if (shouldFilterByOffCanvas && offCanvasCircleIds.has(circle.id)) {
              return false; // Filter out - it belongs to off-canvas
            }
            return true; // Keep it in main canvas
          })
          .map((circle) => ({
            ...circle, // Preserve ALL properties including text, textColor, align, verticalAlign, etc.
            isNew: false,
            fillOpacity: circle.fillOpacity ?? 1,
            strokeOpacity: circle.strokeOpacity ?? 1,
            opacity: 1,
            // CRITICAL: Explicitly preserve text-related properties to ensure they're not lost
            text: circle.text ?? "",
            textColor: circle.textColor,
            align: circle.align ?? "center",
            verticalAlign: circle.verticalAlign ?? "middle",
            fontSize: circle.fontSize,
            fontStyle: circle.fontStyle,
            textDecoration: circle.textDecoration,
          }));

        // Note: Off-canvas circles are restored separately via `offCanvasCircles`
        // below. We intentionally do NOT merge them into `redoCircles` here so
        // that shapes created on the off-canvas remain off-canvas on redo.

        // For circles we keep the logic simple:
        // - Always restore main-canvas circles from `redoCircles`
        // - Off-canvas circles are restored separately from `offCanvasCircles`
        setCircles(redoCircles);

        const restoredDoubleSidedArrows = (
          finalStateToRedo.doubleSidedArrows || []
        ).map((arrow) => {
          // Normalize strokeColor to ensure it's always a string
          const normalizedStroke = normalizeStrokeColor(
            arrow.stroke || arrow.strokeColor,
          );
          return {
            ...arrow,
            stroke: normalizedStroke,
            strokeColor: normalizedStroke,
            angle: arrow.angle ?? 90, // Ensure angle is always present
            isRestored: true,
            isFromHistory: true,
          };
        });
        // When redoing add_double_sided_arrow that had 0 main (off-canvas add), preserve current main doubleSidedArrows.
        const isOffCanvasAddDoubleSidedArrowRedo =
          (finalStateToRedo.operationType === "add_double_sided_arrow" ||
            finalStateToRedo.operationType ===
            "add_double_sided_arrow_clone") &&
          finalStateToRedo.doubleSidedArrows?.length === 0;
        if (isOffCanvasAddDoubleSidedArrowRedo) {
          setDoubleSidedArrows((prev) => prev);
        } else {
          setDoubleSidedArrows(restoredDoubleSidedArrows);
        }

        // Text restore logic: only text-affecting or destructive operations should modify texts.
        const isTextRedoOperation =
          finalStateToRedo.operationType === "add_text" ||
          finalStateToRedo.operationType === "add_text_clone" ||
          finalStateToRedo.operationType === "update_text_content" ||
          finalStateToRedo.operationType === "update_text_properties" ||
          finalStateToRedo.operationType === "update_text_background" ||
          finalStateToRedo.operationType === "text_drag_end" ||
          finalStateToRedo.operationType === "add_part" ||
          finalStateToRedo.operationType === "replacePart" ||
          finalStateToRedo.operationType === "clear_canvas" ||
          finalStateToRedo.operationType === "delete_object" ||
          finalStateToRedo.operationType === "delete_multiple_objects" ||
          finalStateToRedo.operationType === "element_edit_complete";

        if (isTextRedoOperation) {
          let redoTexts = Array.isArray(finalStateToRedo.texts)
            ? finalStateToRedo.texts
            : [];

          const isAddTextOperation =
            finalStateToRedo.operationType === "add_text" ||
            finalStateToRedo.operationType === "add_text_clone";

          // Always restore off-canvas texts from history, merging with current ones.
          let offCanvasTextsFromHistory = Array.isArray(
            finalStateToRedo.offCanvasTexts,
          )
            ? finalStateToRedo.offCanvasTexts
            : [];
          const offCanvasTextIds = new Set(
            offCanvasTextsFromHistory.map((t) => t && t.id).filter(Boolean),
          );

          // CRITICAL FIX: For text-related redo operations, treat the history snapshot
          // as the single source of truth for off-canvas texts. We replace the entire
          // offCanvasTexts state with what is stored in history instead of merging,
          // so that all custom fields (e.g. text.content, text.task, etc.) are
          // restored exactly as they were when the snapshot was taken.
          if (setOffCanvasTexts) {
            setOffCanvasTexts(offCanvasTextsFromHistory);
          }

          // Filter out any texts that belong to off-canvas so they don't get
          // rendered on the main canvas as well.
          let mainRedoTexts = redoTexts.filter(
            (t) => t && !offCanvasTextIds.has(t.id),
          );

          // For add_text operations, ensure the newly added main-canvas text is
          // present even if the snapshot's texts array was saved without it,
          // but only if it is not an off-canvas text.
          if (isAddTextOperation && finalStateToRedo.selectedText) {
            const selectedId = finalStateToRedo.selectedTextId;
            const isOffCanvasSelected =
              selectedId && offCanvasTextIds.has(selectedId);
            const hasSelectedInMain =
              selectedId &&
              mainRedoTexts.some((t) => t && t.id === selectedId);

            if (!isOffCanvasSelected && !hasSelectedInMain) {
              mainRedoTexts = [...mainRedoTexts, finalStateToRedo.selectedText];
            }
          }

          if (isAddTextOperation) {
            // Merge redo texts into existing main texts so API baseline texts
            // are preserved and newly added main-canvas texts are restored.
            setTexts((prev) => {
              const baseTexts = Array.isArray(prev) ? prev : [];
              const existingIds = new Set(baseTexts.map((t) => t.id));
              const mergedTexts = [
                ...baseTexts,
                ...mainRedoTexts.filter((t) => !existingIds.has(t.id)),
              ];
              return mergedTexts;
            });
          } else {
            // For other text-affecting operations (delete, clear_canvas, edits, etc.)
            // we treat the history snapshot (minus off-canvas texts) as the source of truth.
            setTexts(mainRedoTexts);
          }
        }
        // Only restore off-canvas rectangles when the snapshot actually has them.
        const shouldRestoreOffCanvasRectangles =
          (finalStateToRedo.offCanvasRectangles || []).length > 0;

        const redoOffCanvasRectanglesSource =
          shouldRestoreOffCanvasRectangles
            ? finalStateToRedo.offCanvasRectangles || []
            : []; // Empty array for main-canvas-only operations
        // CRITICAL: If a rectangle exists in BOTH arrays (due to a bug in saving), prioritize off-canvas
        // This means: if it's in offCanvasRectangles, keep it there and remove it from main canvas
        // But DON'T remove it from off-canvas just because it's also in main canvas
        // The main canvas filtering above already removed duplicates, so off-canvas should keep all its rectangles
        // CRITICAL: Don't let the same rectangle ID exist in BOTH main and off-canvas arrays.
        // If an ID is present in main (redoRectangles), treat that as the source of truth and
        // filter it out from off-canvas so the rectangle doesn't render twice or "jump" canvases.
        const mainRectIds = new Set(redoRectangles.map((r) => r.id));

        // Preserve ALL properties including width, height, rotation, scaleX, scaleY, etc.
        const redoOffCanvasRectangles = redoOffCanvasRectanglesSource
          .filter((rect) => !mainRectIds.has(rect.id))
          .map((rect) => {
            const restored = {
              ...rect, // Preserve all properties from history first
              isNew: false,
              fillOpacity: rect.fillOpacity ?? 1,
              strokeOpacity: rect.strokeOpacity ?? 1,
              opacity: 1,
              width: rect.width !== undefined ? rect.width : 50,
              height: rect.height !== undefined ? rect.height : 50,
              x: rect.x ?? 0,
              y: rect.y ?? 0,
              rotation: rect.rotation ?? 0,
              scaleX: rect.scaleX ?? 1,
              scaleY: rect.scaleY ?? 1,
              text: rect.text ?? "",
              textColor: rect.textColor,
              align: rect.align ?? "center",
              verticalAlign: rect.verticalAlign ?? "middle",
              fontSize: rect.fontSize,
              fontStyle: rect.fontStyle,
              textDecoration: rect.textDecoration,
            };
            return restored;
          });
        // Preserve existing off-canvas texts; don't override from history snapshot
        setOffCanvasRectangles(redoOffCanvasRectangles);
        // When redoing add_circle that was drawn on off-canvas, history should have saved
        // those in `offCanvasCircles`. We simply trust that array here.
        const shouldRestoreOffCanvasCircles =
          (finalStateToRedo.offCanvasCircles || []).length > 0;

        const redoOffCanvasCirclesSource =
          shouldRestoreOffCanvasCircles
            ? finalStateToRedo.offCanvasCircles || []
            : []; // Empty array for main-canvas-only operations
        // CRITICAL: Don't let the same circle ID exist in BOTH main and off-canvas arrays.
        // If an ID is present in main (redoCircles), treat that as the source of truth and
        // filter it out from off-canvas so the circle doesn't render twice.
        const mainCircleIds = new Set(redoCircles.map((c) => c.id));

        // Preserve ALL properties including text, textColor, align, verticalAlign, radius, etc.
        const redoOffCanvasCircles = redoOffCanvasCirclesSource
          .filter((circle) => !mainCircleIds.has(circle.id))
          .map((circle) => {
            const restored = {
              ...circle, // Preserve all properties from history first
              isNew: false,
              fillOpacity: circle.fillOpacity ?? 1,
              strokeOpacity: circle.strokeOpacity ?? 1,
              opacity: 1,
              // Explicitly preserve radius and other important properties
              radius: circle.radius !== undefined ? circle.radius : 25,
              x: circle.x ?? 0,
              y: circle.y ?? 0,
              rotation: circle.rotation ?? 0,
              scaleX: circle.scaleX ?? 1,
              scaleY: circle.scaleY ?? 1,
              // Explicitly preserve text-related properties to ensure they're not lost
              text: circle.text ?? "",
              textColor: circle.textColor,
              align: circle.align ?? "center",
              verticalAlign: circle.verticalAlign ?? "middle",
              fontSize: circle.fontSize,
              fontStyle: circle.fontStyle,
              textDecoration: circle.textDecoration,
            };

            return restored;
          });
        setOffCanvasCircles(redoOffCanvasCircles);
        setOffCanvasArrows(finalStateToRedo.offCanvasArrows || []);
        setOffCanvasDoubleSidedArrows(
          finalStateToRedo.offCanvasDoubleSidedArrows || [],
        );
        // CRITICAL FIX: Handle off-canvas images with proper image loading (same as main canvas images)
        // Off-canvas images need their image objects loaded, not just set directly
        if (
          finalStateToRedo.offCanvasImages &&
          finalStateToRedo.offCanvasImages.length > 0
        ) {
          // Process off-canvas images similar to main canvas images
          // First, set images immediately with preserved objects to prevent flicker
          const offCanvasImagesWithPreservedObjects =
            finalStateToRedo.offCanvasImages.map((historyImg) => {
              // Find matching image in current main images state by ID (images can move between canvases)
              const currentImg = images?.find(
                (img) => img.id === historyImg.id,
              );

              // Check if image data has changed
              const imageDataChanged =
                currentImg &&
                (historyImg.src !== currentImg.src ||
                  historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                  JSON.stringify(historyImg.cropData) !==
                  JSON.stringify(currentImg.cropData) ||
                  historyImg.originalImageDataUrl !==
                  currentImg.originalImageDataUrl);

              // If we have a matching image with loaded image objects AND image data hasn't changed, reuse them
              if (
                currentImg &&
                currentImg.image &&
                currentImg.originalImage &&
                !imageDataChanged
              ) {
                return {
                  ...historyImg, // Use history data (position, size, etc.)
                  image: currentImg.image, // Preserve loaded image object
                  originalImage: currentImg.originalImage, // Preserve loaded original image object
                  compressedImage: currentImg.compressedImage, // Preserve compressed image if exists
                };
              }

              // Otherwise, return history image as-is (will be loaded async)
              // Remove image objects if they exist so they get reloaded
              const {
                image,
                originalImage,
                compressedImage,
                ...imgWithoutImageObjects
              } = historyImg;
              return imgWithoutImageObjects;
            });

          // Only load images that don't have preserved objects (don't reload existing ones)
          const offCanvasImagesToLoad = finalStateToRedo.offCanvasImages.filter(
            (historyImg) => {
              const currentImg = images?.find(
                (img) => img.id === historyImg.id,
              );

              // Check if image data has changed
              const imageDataChanged =
                currentImg &&
                (historyImg.src !== currentImg.src ||
                  historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                  JSON.stringify(historyImg.cropData) !==
                  JSON.stringify(currentImg.cropData) ||
                  historyImg.originalImageDataUrl !==
                  currentImg.originalImageDataUrl);

              // Load if we don't have a matching image with loaded objects OR if image data changed
              return !(
                currentImg &&
                currentImg.image &&
                currentImg.originalImage &&
                !imageDataChanged
              );
            },
          );

          // CRITICAL: For cropped images, ensure we use imageDataUrl (cropped version) not originalImageDataUrl
          const offCanvasImagesForRestore = offCanvasImagesToLoad.map((img) => {
            // Check if image has cropData (cropped image)
            const hasCropData =
              img.cropData !== null &&
              img.cropData !== undefined &&
              typeof img.cropData === "object" &&
              (Object.keys(img.cropData).length > 0 ||
                img.cropData.left !== undefined ||
                img.cropData.top !== undefined ||
                img.cropData.width !== undefined ||
                img.cropData.height !== undefined);
            const hasImageDataUrl =
              img.imageDataUrl && img.imageDataUrl.trim() !== "";

            // If image has cropData, use imageDataUrl (cropped version) as the source
            if (hasCropData && hasImageDataUrl) {
              // Temporarily remove originalImageDataUrl so restoreImagesFromHistory uses imageDataUrl
              const { originalImageDataUrl, ...imgWithoutOriginal } = img;
              return {
                ...imgWithoutOriginal,
                src: img.imageDataUrl, // Use cropped image data URL
                _originalImageDataUrl: originalImageDataUrl, // Store temporarily
              };
            }
            // Otherwise, use originalImageDataUrl if available, or src
            if (img.originalImageDataUrl) {
              return {
                ...img,
                src: img.originalImageDataUrl,
              };
            }
            // CRITICAL: Use imageDataUrl as src when it's a data URL so redo can load (off-canvas)
            if (hasImageDataUrl && img.imageDataUrl.startsWith("data:")) {
              return { ...img, src: img.imageDataUrl };
            }
            return img;
          });

          if (offCanvasImagesToLoad.length === 0) {
            // All images preserved (e.g. from main canvas) - set immediately
            setOffCanvasImages(offCanvasImagesWithPreservedObjects);
          } else {
            // CRITICAL: Load then set once (no initial set) so ImageElement receives img.image and image is visible
            Promise.all(
              offCanvasImagesForRestore.map((img) =>
                createImageLoadPromise({ imgData: img, info: img }),
              ),
            )
              .then((restoredOffCanvasImages) => {
                if (
                  restoredOffCanvasImages &&
                  restoredOffCanvasImages.length > 0
                ) {
                  const finalOffCanvasImages = (
                    finalStateToRedo.offCanvasImages || []
                  ).map((historyImg) => {
                    const restored = restoredOffCanvasImages.find(
                      (r) => r.id === historyImg.id,
                    );
                    if (restored && restored.image) {
                      const flipH = historyImg.flipHorizontal || false;
                      const flipV = historyImg.flipVertical || false;
                      const isApiImage =
                        historyImg.src &&
                        (historyImg.src.startsWith("http://") ||
                          historyImg.src.startsWith("https://"));
                      const shouldApplyFlipViaScale =
                        isApiImage || !historyImg.hasBakedTransforms;
                      const scaleX =
                        shouldApplyFlipViaScale && flipH
                          ? -Math.abs(historyImg.scaleX || 1)
                          : Math.abs(historyImg.scaleX || 1);
                      const scaleY =
                        shouldApplyFlipViaScale && flipV
                          ? -Math.abs(historyImg.scaleY || 1)
                          : Math.abs(historyImg.scaleY || 1);
                      // Off-canvas: prefer history dimensions; when history has cropData but missing dimensions, use restored (loaded image natural size)
                      const isCropped =
                        historyImg.cropData != null &&
                        typeof historyImg.cropData === "object";
                      const fromRestored = (n) =>
                        typeof n === "number" && n > 0 ? n : 100;
                      const w =
                        typeof historyImg.width === "number" &&
                          historyImg.width > 0
                          ? historyImg.width
                          : fromRestored(restored.width);
                      const h =
                        typeof historyImg.height === "number" &&
                          historyImg.height > 0
                          ? historyImg.height
                          : fromRestored(restored.height);
                      const origW =
                        typeof historyImg.originalWidth === "number" &&
                          historyImg.originalWidth > 0
                          ? historyImg.originalWidth
                          : isCropped
                            ? w
                            : fromRestored(restored.originalWidth) || w;
                      const origH =
                        typeof historyImg.originalHeight === "number" &&
                          historyImg.originalHeight > 0
                          ? historyImg.originalHeight
                          : isCropped
                            ? h
                            : fromRestored(restored.originalHeight) || h;
                      // Build from history so no property from restored overwrites display/crop metadata
                      return {
                        ...historyImg,
                        width: w,
                        height: h,
                        originalWidth: origW,
                        originalHeight: origH,
                        cropData: historyImg.cropData,
                        imageDataUrl: historyImg.imageDataUrl,
                        originalImageDataUrl: historyImg.originalImageDataUrl,
                        image: restored.image,
                        originalImage: restored.originalImage || restored.image,
                        compressedImage: restored.compressedImage,
                        flipHorizontal: flipH,
                        flipVertical: flipV,
                        scaleX,
                        scaleY,
                        src: historyImg.imageDataUrl || historyImg.src,
                      };
                    }
                    const {
                      image: _i,
                      originalImage: _o,
                      compressedImage: _c,
                      ...rest
                    } = historyImg;
                    return rest;
                  });
                  setOffCanvasImages(finalOffCanvasImages);
                } else {
                  console.warn(
                    "⚠️ [REDO] No restored off-canvas images returned",
                  );
                  setOffCanvasImages(offCanvasImagesWithPreservedObjects);
                }
              })
              .catch((error) => {
                console.error(
                  "❌ [REDO] Error loading off-canvas images:",
                  error,
                );
                setOffCanvasImages(offCanvasImagesWithPreservedObjects);
              });
          }
        } else {
          // No off-canvas images in the state to restore
          setOffCanvasImages([]);
        }
        setOffCanvasScribbles(finalStateToRedo.offCanvasScribbles || []);
        setOffCanvasLines(finalStateToRedo.offCanvasLines || []);
        setOffCanvasVideos(finalStateToRedo.offCanvasVideos || []);

        // CRITICAL: Create Set of off-canvas image IDs BEFORE cleanup
        // This ensures we can filter main canvas images in the cleanup section
        const offCanvasImageIds = new Set(
          (finalStateToRedo.offCanvasImages || []).map((img) => img.id),
        );

        // CRITICAL FIX: Final cleanup - ensure no images exist in both main and off-canvas.
        // Run whenever the redo state has off-canvas images (not only drag_transfer), so we
        // remove duplicates even when history saved with image in both (e.g. element_edit_complete).
        if (offCanvasImageIds.size > 0) {
          // Immediately filter main canvas images (synchronous)
          setImages((prevImages) => {
            const filtered = (prevImages || []).filter(
              (img) => !offCanvasImageIds.has(img.id),
            );
            
            return filtered;
          });

          // Also use setTimeout to ensure this runs after all async image loading completes
          setTimeout(() => {
            setImages((prevImages) => {
              const filtered = (prevImages || []).filter(
                (img) => !offCanvasImageIds.has(img.id),
              );
              return filtered;
            });
          }, 100); // Small delay to ensure async loading completes

          // And another one with longer delay as final safety net
          setTimeout(() => {
            setImages((prevImages) => {
              const filtered = (prevImages || []).filter(
                (img) => !offCanvasImageIds.has(img.id),
              );
              return filtered;
            });
          }, 500); // Longer delay for any slow async operations
        }

        // Restore selectedText state if it exists in history
        if (finalStateToRedo.selectedText) {
          setSelectedText(finalStateToRedo.selectedText);
        }

        // Restore selectedTextId if it exists in history
        if (finalStateToRedo.selectedTextId) {
          setSelectedTextId(finalStateToRedo.selectedTextId);
        }

        // Restore canvas height if it exists in history
        if (finalStateToRedo.canvasHeight !== undefined && setCanvasHeight) {
          setCanvasHeight(String(finalStateToRedo.canvasHeight));
        }

        // Handle images - preserve existing image objects to prevent flicker
        // CRITICAL FIX: Only filter out images that are in offCanvasImages for drag_transfer operations
        // For normal operations (add_image, etc.), images in the images array belong to main canvas
        // This matches the pattern used for circles and rectangles

        // DEBUG: Log what's in the history for transfer operations
        if (shouldFilterByOffCanvas) {
        }

        // DEBUG: Log isOnTopLayer property for all images in history
        // CRITICAL: An image must never appear on both main and off-canvas. If the redo state
        // has the same image in both (e.g. element_edit_complete after transfer saved with stale state),
        // treat it as off-canvas only.
        const mainCanvasImages = (finalStateToRedo.images || [])
          .filter((img) => {
            if (offCanvasImageIds.has(img.id)) {
              return false; // Never show on main if it's in off-canvas
            }
            return true;
          })
          .map((img) => {
            // CRITICAL: When restoring images to main canvas, ensure isOnTopLayer is true
            // This fixes the issue where images dragged to off-canvas and then undone
            // would have isOnTopLayer: false and not render properly
            return {
              ...img,
              isOnTopLayer: true, // Always set to true for main canvas images
            };
          });

        if (mainCanvasImages.length > 0) {
          // Match images by ID and reuse image objects from current state to prevent flicker
          const imagesWithPreservedObjects = mainCanvasImages.map(
            (historyImg) => {
              // Find matching image in current state by ID
              const currentImg = images?.find(
                (img) => img.id === historyImg.id,
              );

              // CRITICAL: Check if image data has changed (src, imageDataUrl, or cropData)
              // If image data changed (e.g., after crop), we must reload it, not reuse old object
              const imageDataChanged =
                currentImg &&
                (historyImg.src !== currentImg.src ||
                  historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                  JSON.stringify(historyImg.cropData) !==
                  JSON.stringify(currentImg.cropData) ||
                  historyImg.originalImageDataUrl !==
                  currentImg.originalImageDataUrl);

              // If we have a matching image with loaded image objects AND image data hasn't changed, reuse them
              // This preserves images even when their position changes (e.g., after drag)
              // But reloads them if the image data itself changed (e.g., after crop)
              if (
                currentImg &&
                currentImg.image &&
                currentImg.originalImage &&
                !imageDataChanged
              ) {
                return {
                  ...historyImg, // Use history data (position, size, etc.)
                  image: currentImg.image, // Preserve loaded image object
                  originalImage: currentImg.originalImage, // Preserve loaded original image object
                  compressedImage: currentImg.compressedImage, // Preserve compressed image if exists
                };
              }

              // Otherwise, return history image as-is (will be loaded async)
              // CRITICAL: Remove image objects if they exist so they get reloaded
              const {
                image,
                originalImage,
                compressedImage,
                ...imgWithoutImageObjects
              } = historyImg;
              return imgWithoutImageObjects;
            },
          );

          // Set images immediately with preserved objects to prevent flicker
          // CRITICAL FIX: For transfer operations, double-check we're not including images that should be in off-canvas
          // This is a safety net in case mainCanvasImages wasn't filtered correctly
          const finalImagesToSet = shouldFilterByOffCanvas
            ? imagesWithPreservedObjects.filter(
              (img) => !offCanvasImageIds.has(img.id),
            )
            : imagesWithPreservedObjects;

          setImages(finalImagesToSet);

          // Only load images that don't have preserved objects (don't reload existing ones)
          // CRITICAL: Also reload if image data changed (e.g., after crop)
          // Use mainCanvasImages (already filtered) instead of finalStateToRedo.images
          const imagesToLoad = mainCanvasImages.filter((historyImg) => {
            const currentImg = images?.find((img) => img.id === historyImg.id);

            // Check if image data has changed
            const imageDataChanged =
              currentImg &&
              (historyImg.src !== currentImg.src ||
                historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                JSON.stringify(historyImg.cropData) !==
                JSON.stringify(currentImg.cropData) ||
                historyImg.originalImageDataUrl !==
                currentImg.originalImageDataUrl);

            // Load if we don't have a matching image with loaded objects OR if image data changed
            return !(
              currentImg &&
              currentImg.image &&
              currentImg.originalImage &&
              !imageDataChanged
            );
          });

          // DEBUG: Log what's in imagesToLoad to see if cropData is preserved
          if (imagesToLoad.length > 0) {
            imagesToLoad.forEach((img, index) => { });
          }

          // CRITICAL: For cropped images, ensure we use imageDataUrl (cropped version) not originalImageDataUrl
          // restoreImagesFromHistory prioritizes originalImageDataUrl, so we need to temporarily remove it
          // for cropped images so it uses imageDataUrl instead
          const imagesForRestore = imagesToLoad.map((img) => {
            // Check if image has cropData (cropped image)
            // cropData can be an object with left, top, width, height properties
            const hasCropData =
              img.cropData !== null &&
              img.cropData !== undefined &&
              typeof img.cropData === "object" &&
              (Object.keys(img.cropData).length > 0 ||
                img.cropData.left !== undefined ||
                img.cropData.top !== undefined ||
                img.cropData.width !== undefined ||
                img.cropData.height !== undefined);
            const hasImageDataUrl =
              img.imageDataUrl && img.imageDataUrl.trim() !== "";

            // If image has cropData, use imageDataUrl (cropped version) as the source
            if (hasCropData && hasImageDataUrl) {
              // Temporarily remove originalImageDataUrl so restoreImagesFromHistory uses imageDataUrl
              const { originalImageDataUrl, ...imgWithoutOriginal } = img;
              return {
                ...imgWithoutOriginal,
                src: img.imageDataUrl, // Use cropped image data URL
                // CRITICAL: Don't include originalImageDataUrl so restoreImagesFromHistory uses imageDataUrl
                // But preserve it in a different property so we can restore it later
                _originalImageDataUrl: originalImageDataUrl, // Store temporarily
              };
            }
            // Otherwise, use originalImageDataUrl if available, or src
            if (img.originalImageDataUrl) {
              return {
                ...img,
                src: img.originalImageDataUrl,
              };
            }
            return img;
          });

          // Load missing images asynchronously in background (non-blocking)
          if (imagesToLoad.length > 0) {
            Promise.resolve(imagesForRestore)
              .then((imagesToActuallyLoad) =>
                Promise.all(
                  (imagesToActuallyLoad || []).map((img) =>
                    createImageLoadPromise({ imgData: img, info: img }),
                  ),
                ),
              )
              .then((restoredImages) => {
                // Merge restored images with preserved ones
                // Use mainCanvasImages (already filtered) instead of finalStateToRedo.images
                if (restoredImages && restoredImages.length > 0) {
                  const finalImages = mainCanvasImages.map((historyImg) => {
                    const currentImg = images?.find(
                      (img) => img.id === historyImg.id,
                    );

                    // CRITICAL: Check if image data has changed
                    const imageDataChanged =
                      currentImg &&
                      (historyImg.src !== currentImg.src ||
                        historyImg.imageDataUrl !== currentImg.imageDataUrl ||
                        JSON.stringify(historyImg.cropData) !==
                        JSON.stringify(currentImg.cropData) ||
                        historyImg.originalImageDataUrl !==
                        currentImg.originalImageDataUrl);

                    // If we already have a loaded image object AND image data hasn't changed, keep it
                    // Otherwise, use the restored image (which has the correct image data)
                    if (
                      currentImg &&
                      currentImg.image &&
                      currentImg.originalImage &&
                      !imageDataChanged
                    ) {
                      // CRITICAL: Still need to ensure flip properties and scale are correct
                      const isApiImage =
                        historyImg.src &&
                        (historyImg.src.startsWith("http://") ||
                          historyImg.src.startsWith("https://"));
                      const shouldApplyFlipViaScale =
                        isApiImage || !historyImg.hasBakedTransforms;
                      const flipH = historyImg.flipHorizontal || false;
                      const flipV = historyImg.flipVertical || false;
                      const scaleX =
                        shouldApplyFlipViaScale && flipH
                          ? -Math.abs(historyImg.scaleX || 1)
                          : Math.abs(historyImg.scaleX || 1);
                      const scaleY =
                        shouldApplyFlipViaScale && flipV
                          ? -Math.abs(historyImg.scaleY || 1)
                          : Math.abs(historyImg.scaleY || 1);

                      return {
                        ...historyImg,
                        image: currentImg.image,
                        originalImage: currentImg.originalImage,
                        compressedImage: currentImg.compressedImage,
                        flipHorizontal: flipH,
                        flipVertical: flipV,
                        scaleX: scaleX,
                        scaleY: scaleY,
                      };
                    }
                    // Otherwise, find the restored image (which has the correct image data loaded)
                    const restoredImg = restoredImages.find(
                      (img) => img.id === historyImg.id,
                    );
                    if (restoredImg && restoredImg.image) {
                      // CRITICAL: Use historyImg as base to preserve ALL properties including cropData
                      // Then merge in the loaded image objects
                      // CRITICAL: Calculate scaleX/scaleY based on flipHorizontal/flipVertical
                      const isApiImage =
                        historyImg.src &&
                        (historyImg.src.startsWith("http://") ||
                          historyImg.src.startsWith("https://"));
                      const shouldApplyFlipViaScale =
                        isApiImage || !historyImg.hasBakedTransforms;
                      const flipH = historyImg.flipHorizontal || false;
                      const flipV = historyImg.flipVertical || false;
                      const scaleX =
                        shouldApplyFlipViaScale && flipH
                          ? -Math.abs(historyImg.scaleX || 1)
                          : Math.abs(historyImg.scaleX || 1);
                      const scaleY =
                        shouldApplyFlipViaScale && flipV
                          ? -Math.abs(historyImg.scaleY || 1)
                          : Math.abs(historyImg.scaleY || 1);

                      // CRITICAL: Restore originalImageDataUrl if it was temporarily stored
                      const restoredOriginalImageDataUrl =
                        historyImg._originalImageDataUrl ||
                        historyImg.originalImageDataUrl;

                      const finalImg = {
                        ...historyImg, // Use history data (includes all properties like cropData, imageDataUrl, etc.)
                        image: restoredImg.image,
                        originalImage:
                          restoredImg.originalImage || restoredImg.image,
                        compressedImage: restoredImg.compressedImage,
                        // CRITICAL: Preserve all crop-related properties from history
                        cropData: historyImg.cropData,
                        originalCropRotation: historyImg.originalCropRotation,
                        flipHorizontal: flipH,
                        flipVertical: flipV,
                        hasBakedTransforms: historyImg.hasBakedTransforms,
                        scaleX: scaleX,
                        scaleY: scaleY,
                        // Use imageDataUrl from history (cropped version) as src if it exists
                        src: historyImg.imageDataUrl || historyImg.src,
                        imageDataUrl: historyImg.imageDataUrl,
                        originalImageDataUrl: restoredOriginalImageDataUrl,
                        // Remove temporary property if it exists
                        _originalImageDataUrl: undefined,
                      };
                      return finalImg;
                    }
                    // Fallback: return historyImg with all properties preserved, but ensure scaleX/scaleY are correct
                    const isApiImage =
                      historyImg.src &&
                      (historyImg.src.startsWith("http://") ||
                        historyImg.src.startsWith("https://"));
                    const shouldApplyFlipViaScale =
                      isApiImage || !historyImg.hasBakedTransforms;
                    const flipH = historyImg.flipHorizontal || false;
                    const flipV = historyImg.flipVertical || false;
                    const scaleX =
                      shouldApplyFlipViaScale && flipH
                        ? -Math.abs(historyImg.scaleX || 1)
                        : Math.abs(historyImg.scaleX || 1);
                    const scaleY =
                      shouldApplyFlipViaScale && flipV
                        ? -Math.abs(historyImg.scaleY || 1)
                        : Math.abs(historyImg.scaleY || 1);

                    return {
                      ...historyImg,
                      flipHorizontal: flipH,
                      flipVertical: flipV,
                      scaleX: scaleX,
                      scaleY: scaleY,
                    };
                  });
                  // CRITICAL FIX: For transfer operations, filter out images that should be in off-canvas
                  const finalImagesToSet = shouldFilterByOffCanvas
                    ? finalImages.filter(
                      (img) => !offCanvasImageIds.has(img.id),
                    )
                    : finalImages;
                  setImages(finalImagesToSet);
                } else {
                  console.warn("⚠️ [REDO] No restored images returned");
                }
              })
              .catch((error) => {
                console.error("❌ [REDO] Error loading images:", error);
                // Silently fail - images already set with preserved objects
              });
          }
        } else {
          // No main canvas images to restore (either empty or all in off-canvas)
          // CRITICAL: Set main to definitive list so image doesn't stay on main when redo state
          // had same image in both (e.g. element_edit_complete after transfer with stale snapshot).
          if (
            shouldFilterByOffCanvas ||
            (offCanvasImageIds.size > 0 && mainCanvasImages.length === 0)
          ) {
            setImages(mainCanvasImages);
          } else if (
            finalStateToRedo.images &&
            finalStateToRedo.images.length === 0
          ) {
            // Explicitly empty array in history - clear main canvas images
            setImages([]);
          }
          // CRITICAL: Don't clear images if finalStateToRedo.images is undefined/null
          // Only clear if it's explicitly an empty array (meaning the state intentionally had no images)
          // If finalStateToRedo.images is undefined, it might mean the state wasn't properly saved
          // In that case, try to get images from the previous history state
          if (
            finalStateToRedo.images === undefined ||
            finalStateToRedo.images === null
          ) {
            // Try to get images from the previous history state
            const previousHistoryState =
              history && history.length > 0
                ? history[history.length - 1]
                : null;
            if (
              previousHistoryState &&
              previousHistoryState.images &&
              previousHistoryState.images.length > 0
            ) {
              // Use images from previous state
              const imagesToRestore = previousHistoryState.images.map((img) => {
                const currentImg = images?.find((i) => i.id === img.id);
                if (
                  currentImg &&
                  currentImg.image &&
                  currentImg.originalImage
                ) {
                  return {
                    ...img,
                    image: currentImg.image,
                    originalImage: currentImg.originalImage,
                    compressedImage: currentImg.compressedImage,
                  };
                }
                const {
                  image,
                  originalImage,
                  compressedImage,
                  ...imgWithoutImageObjects
                } = img;
                return imgWithoutImageObjects;
              });
              setImages(imagesToRestore);
              // Load images that need to be loaded
              const imagesToLoad = imagesToRestore.filter((img) => {
                const currentImg = images?.find((i) => i.id === img.id);
                return !(
                  currentImg &&
                  currentImg.image &&
                  currentImg.originalImage
                );
              });
              if (imagesToLoad.length > 0) {
                Promise.resolve(imagesToLoad).then((imagesToActuallyLoad) =>
                  Promise.all(
                    (imagesToActuallyLoad || []).map((img) =>
                      createImageLoadPromise({ imgData: img, info: img }),
                    ),
                  ).then((restoredImages) => {
                    if (restoredImages && restoredImages.length > 0) {
                      const finalImages = imagesToRestore.map((historyImg) => {
                        const currentImg = images?.find(
                          (i) => i.id === historyImg.id,
                        );
                        if (
                          currentImg &&
                          currentImg.image &&
                          currentImg.originalImage
                        ) {
                          return {
                            ...historyImg,
                            image: currentImg.image,
                            originalImage: currentImg.originalImage,
                            compressedImage: currentImg.compressedImage,
                          };
                        }
                        const restoredImg = restoredImages.find(
                          (img) => img.id === historyImg.id,
                        );
                        if (restoredImg && restoredImg.image) {
                          return {
                            ...historyImg,
                            image: restoredImg.image,
                            originalImage: restoredImg.originalImage,
                            compressedImage: restoredImg.compressedImage,
                          };
                        }
                        return historyImg;
                      });
                      setImages(finalImages);
                    }
                  }),
                );
              }
            } else {
              console.warn(
                "⚠️ [REDO] finalStateToRedo.images is undefined/null and no previous state images found, preserving current images",
              );
            }
          } else if (finalStateToRedo.images.length === 0) {
            // Empty array - check if this is an element_edit_complete that might have been saved incorrectly
            // If it's element_edit_complete and we have images in the previous history state, use those
            if (finalStateToRedo.operationType === "element_edit_complete") {
              const previousHistoryState =
                history && history.length > 0
                  ? history[history.length - 1]
                  : null;
              if (
                previousHistoryState &&
                previousHistoryState.images &&
                previousHistoryState.images.length > 0
              ) {
                // Use images from previous state
                const imagesToRestore = previousHistoryState.images.map(
                  (img) => {
                    const currentImg = images?.find((i) => i.id === img.id);
                    if (
                      currentImg &&
                      currentImg.image &&
                      currentImg.originalImage
                    ) {
                      return {
                        ...img,
                        image: currentImg.image,
                        originalImage: currentImg.originalImage,
                        compressedImage: currentImg.compressedImage,
                      };
                    }
                    const {
                      image,
                      originalImage,
                      compressedImage,
                      ...imgWithoutImageObjects
                    } = img;
                    return imgWithoutImageObjects;
                  },
                );
                setImages(imagesToRestore);
                // Load images that need to be loaded
                const imagesToLoad = imagesToRestore.filter((img) => {
                  const currentImg = images?.find((i) => i.id === img.id);
                  return !(
                    currentImg &&
                    currentImg.image &&
                    currentImg.originalImage
                  );
                });
                if (imagesToLoad.length > 0) {
                  Promise.resolve(imagesToLoad).then((restoredImages) => {
                    if (restoredImages && restoredImages.length > 0) {
                      const finalImages = imagesToRestore.map((historyImg) => {
                        const currentImg = images?.find(
                          (i) => i.id === historyImg.id,
                        );
                        if (
                          currentImg &&
                          currentImg.image &&
                          currentImg.originalImage
                        ) {
                          return {
                            ...historyImg,
                            image: currentImg.image,
                            originalImage: currentImg.originalImage,
                            compressedImage: currentImg.compressedImage,
                          };
                        }
                        const restoredImg = restoredImages.find(
                          (img) => img.id === historyImg.id,
                        );
                        if (restoredImg && restoredImg.image) {
                          return {
                            ...historyImg,
                            image: restoredImg.image,
                            originalImage: restoredImg.originalImage,
                            compressedImage: restoredImg.compressedImage,
                          };
                        }
                        return historyImg;
                      });
                      setImages(finalImages);
                    }
                  });
                }
              } else {
                // No previous state images - clear all images
                setImages([]);
              }
            } else {
              // Not element_edit_complete - this means the state intentionally had no images
              // CRITICAL: Clear all images when redoing to a state with no images
              setImages([]);
            }
          } else {
            // Should not reach here, but just in case
            setImages([]);
          }
        }
      };

      // Execute the restoration synchronously for instant response
      restoreCanvasState();

      // DEBUG: Log after redo restoration
    } catch (error) {
      // Error handled
    } finally {
      // Defer reset (same as undo) so any setTimeout(0) from updateImageProperties skips saveToHistory
      setTimeout(() => {
        isUndoingRedoing.current = false;
        if (lastUndoRedoCompletionTime) {
          lastUndoRedoCompletionTime.current = Date.now();
        }
      }, 10);
    }
  };
}
